import { audioFilePath, closeSession, coverUrl, getItem, getProgress, startPlaybackSession, syncSession, updateProgress } from '../api/abs';
import { patchProgress, progressStore } from '../api/progressStore';
import { AbsError, ensureFreshToken, mediaUrl, refreshAccessToken, tokenExpiresAt } from '../api/client';
import { getSession } from '../api/session';
import { PlayMethod, type Chapter, type LibraryItem, type MediaProgress, type PlaybackSession } from '../api/types';
import { resolveServerPath } from '../api/url';
import { openLocalPlayback, quarantineDownload, type LocalPlayback } from '../downloads/manager';
import { notify } from '../lib/notify';
import { platform } from '../lib/device';
import { createStore, storage } from '../lib/store';
import { getBookSpeedScope, normalizePlaybackSpeed, settingsStore, updateSettings } from '../settings';
import { PlayerEvents, type SeekKind, type SleepCancelReason } from './events';
import { flushPendingProgress, getLocalProgress, pickResumePosition, saveLocalProgress, markSynced } from './progress';
import { closeLocalListening, flushLocalListening, recordLocalListening, type LocalSessionBook } from './localSessions';
import { PlaybackStartupTrace, playbackStartupHistory, type PlaybackStartupSnapshot } from './startupTrace';
import { audioSessionOf, InterruptionTracker } from './interruption';
import { chapterIndexAt, chaptersFromTracks, locateTrack, smartRewindSeconds, supportedMimeTypes, type PlayerTrack } from './tracks';
import type { RemoteHost, RemotePlayback, RemoteStatus } from './remote';
import type HlsType from 'hls.js';

export type SleepMode = 'off' | 'timer' | 'chapter';

export interface SleepState {
  mode: SleepMode;
  /** Wall-clock end of a countdown (`timer`). */
  endsAt: number | null;
  /**
   * `chapter` mode: the chapter whose end pauses playback (index into
   * `chapters`) and that end in book seconds. Null when the book has no
   * chapters (the end of the file pauses instead).
   */
  chapterIndex?: number | null;
  chapterEnd?: number | null;
}

/** Why playback is unavailable; `message` is the sentence shown to the listener. */
export type PlayerErrorCode =
  /** Offline, and the book needs the network (not downloaded, or the stream dropped). */
  | 'offline'
  /** The server could not be reached. */
  | 'network'
  /** The server did not answer in time while playback started. */
  | 'timeout'
  /** Signed out / token rejected (401). */
  | 'unauthorized'
  /** The account may not open this book (403). */
  | 'forbidden'
  /** The book is gone from the server, or has no playable audio. */
  | 'notFound'
  /** The browser cannot play the audio format. */
  | 'unsupportedFormat'
  /** A downloaded copy could not be played (incomplete or corrupt). */
  | 'downloadUnplayable'
  /** Stream recovery gave up after repeated drops. */
  | 'streamDropped'
  /** The audio element failed for another reason. */
  | 'playbackFailed'
  | 'unknown';

/** What the listener can do about an error (Flutter's AppErrorAction). */
export type PlayerErrorAction = 'retry' | 'checkConnection' | 'relogin' | 'reDownload' | 'none';

export interface PlayerError {
  code: PlayerErrorCode;
  message: string;
  action: PlayerErrorAction;
  /** True when playback gave up (status `error`); false for a recoverable notice (offline while streaming). */
  fatal: boolean;
  /** HTTP status behind the error, when there was one. */
  status?: number;
}

const ERROR_ACTIONS: Record<PlayerErrorCode, PlayerErrorAction> = {
  offline: 'checkConnection',
  network: 'checkConnection',
  timeout: 'retry',
  unauthorized: 'relogin',
  forbidden: 'relogin',
  notFound: 'none',
  unsupportedFormat: 'none',
  downloadUnplayable: 'reDownload',
  streamDropped: 'checkConnection',
  playbackFailed: 'checkConnection',
  unknown: 'retry',
};

export function playerError(code: PlayerErrorCode, message: string, opts: { fatal?: boolean; status?: number } = {}): PlayerError {
  return { code, message, action: ERROR_ACTIONS[code], fatal: opts.fatal ?? true, ...(opts.status != null ? { status: opts.status } : {}) };
}

/** A load failure with a known cause (thrown inside the engine, classified by `classifyPlayerError`). */
class PlayerFailure extends Error {
  constructor(
    readonly code: PlayerErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'PlayerFailure';
  }
}

export interface PlayerState {
  status: 'idle' | 'loading' | 'ready' | 'error';
  itemId: string | null;
  title: string;
  author: string;
  coverUrl: string;
  chapters: Chapter[];
  duration: number;
  currentTime: number;
  isPlaying: boolean;
  isBuffering: boolean;
  rate: number;
  source: 'stream' | 'local' | null;
  /** True when an audio source is attached (false after Stop or on a cold start). */
  loaded: boolean;
  /** The error message shown to the listener (`errorInfo.message`), or null. */
  error: string | null;
  /** The error with its code and recommended action, or null. Always set together with `error`. */
  errorInfo: PlayerError | null;
  sleep: SleepState;
  finished: boolean;
}

const INITIAL: PlayerState = {
  status: 'idle',
  itemId: null,
  title: '',
  author: '',
  coverUrl: '',
  chapters: [],
  duration: 0,
  currentTime: 0,
  isPlaying: false,
  isBuffering: false,
  rate: 1,
  source: null,
  loaded: false,
  error: null,
  errorInfo: null,
  sleep: { mode: 'off', endsAt: null },
  finished: false,
};

const LAST_PLAYED_KEY = 'plume.lastPlayed.v1';
/** Chapter jumps land 50 ms in, so the playhead is safely inside the chapter (Flutter). */
const CHAPTER_LANDING_OFFSET = 0.05;
const SERVER_SYNC_INTERVAL_MS = 15000;
const LOCAL_SAVE_INTERVAL_MS = 5000;
const SLEEP_FADE_SECONDS = 10;
const MAX_TIMEOUT_MS = 2 ** 31 - 1;
/** A stream that makes no progress for this long while it should be playing is reloaded. */
const STALL_TIMEOUT_MS = 30000;
const WATCHDOG_INTERVAL_MS = 5000;
/** Recovery attempts (token refresh, URL rebuild, new session) before giving up. */
const MAX_RECOVERIES = 6;
/** A paused book whose server position this device last learned longer ago than this is checked against the server when it resumes. */
const RESUME_CHECK_AFTER_MS = 30_000;
/** How long syncs wait on that check before going ahead without it. */
const RESUME_CHECK_TIMEOUT_MS = 3000;
/**
 * hls.js forward buffer for transcoded books, seconds: Flutter's 20-minute
 * target (audio_player_handler.dart `_forwardBufferTarget`, ENG-023). Direct
 * files buffer as the browser decides.
 */
export const HLS_FORWARD_BUFFER_SECONDS = 20 * 60;
/** Already-played audio hls.js keeps for skipping back, seconds. */
const HLS_BACK_BUFFER_SECONDS = 90;

/** Flutter's startup copy (player_provider.dart:1161-1185, spec 07 §15.4; ENG-007). */
export const STARTUP_TIMED_OUT = 'Playback start timed out. Check your network/server and retry.';
export const STARTUP_UNAUTHORIZED = 'Playback authorization failed. Please reconnect and try again.';
export const STARTUP_FAILED = 'Unable to start playback. Please retry.';

interface LastPlayed {
  itemId: string;
  title: string;
  author: string;
  currentTime: number;
  duration: number;
  serverUrl: string;
  /** The book's chapters, so a restored book can navigate and show them before it loads. */
  chapters?: Chapter[];
}

/** Chapters as stored with LastPlayed (only the fields the player reads). */
function storableChapters(chapters: readonly Chapter[]): Chapter[] {
  return chapters.map(({ id, start, end, title }) => ({ id, start, end, title }));
}

function restoredChapters(value: unknown): Chapter[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (c): c is Chapter => !!c && typeof c === 'object' && Number.isFinite((c as Chapter).start) && Number.isFinite((c as Chapter).end),
  );
}

/** Chapters of a preview item, built like a session's (markers, else one per file). */
function previewChapters(preview: LibraryItem | undefined): Chapter[] {
  const media = preview?.media;
  if (!media) return [];
  if (media.chapters?.length) return media.chapters;
  return media.tracks?.length ? chaptersFromTracks(media.tracks) : [];
}

export interface LoadOptions {
  autoplay?: boolean;
  /** Start position in seconds; defaults to the saved progress. */
  startAt?: number;
  /** Item data already on screen, used to show the player instantly. */
  preview?: LibraryItem;
}

/** A background load of the restored book (`preload`); a tap on Play while it runs joins it. */
interface Preload {
  token: number;
  itemId: string;
  /** The listener tapped Play while it ran: it plays once the source is attached. */
  play: boolean;
}

/**
 * How long iOS keeps the lock-screen controls of a paused book (see `holdLockScreen`).
 * 0 elsewhere: other platforms keep a paused page's media notification on their own.
 */
export const PAUSED_LOCK_SCREEN_HOLD_MS = 30 * 60_000;

/** A silent WAV: 0.05 s unlocks the audio element inside a user gesture (iOS); 10 s loops to hold the lock screen. */
function silentWavUrl(seconds = 0.05): string {
  const sampleRate = 8000;
  const samples = Math.round(sampleRate * seconds);
  const buf = new ArrayBuffer(44 + samples);
  const v = new DataView(buf);
  const str = (o: number, s: string) => [...s].forEach((c, i) => v.setUint8(o + i, c.charCodeAt(0)));
  str(0, 'RIFF');
  v.setUint32(4, 36 + samples, true);
  str(8, 'WAVE');
  str(12, 'fmt ');
  v.setUint32(16, 16, true);
  v.setUint16(20, 1, true);
  v.setUint16(22, 1, true);
  v.setUint32(24, sampleRate, true);
  v.setUint32(28, sampleRate, true);
  v.setUint16(32, 1, true);
  v.setUint16(34, 8, true);
  str(36, 'data');
  v.setUint32(40, samples, true);
  for (let i = 0; i < samples; i++) v.setUint8(44 + i, 128);
  return URL.createObjectURL(new Blob([buf], { type: 'audio/wav' }));
}

export class Player implements RemoteHost {
  readonly store = createStore<PlayerState>({ ...INITIAL });
  /** Typed notifications (play, pause, seek, chapter, rate, sleep, buffering, errors) for history and UI. */
  readonly events = new PlayerEvents();
  private audio: HTMLAudioElement | null = null;
  private tracks: PlayerTrack[] = [];
  /** Server-relative content paths, kept so URLs can be rebuilt after a token refresh. */
  private trackPaths: string[] = [];
  private trackIndex = 0;
  private pendingSeek: number | null = null;
  private session: PlaybackSession | null = null;
  private local: LocalPlayback | null = null;
  private hls: HlsType | null = null;
  private loadToken = 0;
  private listenedSinceSync = 0;
  private lastTickAt = 0;
  private lastServerSync = 0;
  private lastLocalSave = 0;
  private lastStoreUpdate = 0;
  private pausedAt = 0;
  private errorRetries = 0;
  private switchingTrack = false;
  private unlockUrl: string | null = null;
  /** Plays silence while a book is paused so iOS keeps its lock-screen controls (see `holdLockScreen`). */
  private holdAudio: HTMLAudioElement | null = null;
  private holdTimer: ReturnType<typeof setTimeout> | null = null;
  private holding = false;
  /** @internal How long a pause holds the lock screen; 0 turns it off (non-iOS, tests). */
  pausedHoldMs = platform.isIOS ? PAUSED_LOCK_SCREEN_HOLD_MS : 0;
  private wantPlaying = false;
  /** A pause the system made (a call, another app's audio) that may resume on its own (ENG-019, `interruption.ts`). */
  private readonly interruption = new InterruptionTracker();
  private recovering = false;
  private lastRecoveryAt = 0;
  /** performance.now() when the playback position last moved (or a load started). */
  private lastProgressAt = 0;
  private lastProgressPos = -1;
  private lastTokenCheck = 0;
  private mediaErrorRecoveries = 0;
  /** Set when the browser could not decode a direct-play file, so ABS transcodes instead. */
  private forceTranscode = false;
  /** Files of the current book the browser has opened, so their format is fine. */
  private decodedTracks = new Set<number>();
  /** Book whose `play` event was emitted and not yet matched by a `pause`. */
  private playingItemId: string | null = null;
  /** True while stop() runs, so the pause it causes is reported as a stop. */
  private stopping = false;
  /** Describes the seek that the next seek() call performs (skip, chapter navigation, smart rewind). */
  private nextSeek: { kind: SeekKind; chapterIndex: number } | null = null;
  private chapterTrack: { itemId: string | null; chapters: Chapter[]; index: number } = { itemId: null, chapters: [], index: -1 };
  /** Element volume ceiling (0..1) set by audio effects; sleep fades scale it. */
  private outputVolume = 1;
  /**
   * A position picked while the book was not loaded (a restored book, or one
   * still loading): the next load of that book starts there. `serial` orders it
   * against loads that begin later. A skip carries `delta`: until the book loads,
   * its position is only what this device last showed, which another device may
   * have moved past, so the skip moves from wherever the load resumes instead.
   */
  private unloadedSeek: { itemId: string; at: number; serial: number; delta?: number } | null = null;
  private seekSerial = 0;
  /** Counts every seek, so an async check can tell the listener moved the playhead meanwhile. */
  private seekCount = 0;
  /**
   * Where the server has the loaded book, as far as this device knows (what the
   * load read, or this device's last confirmed sync), and when that was learned.
   * A paused playhead still at this position has nothing to save, and saving it
   * would rewind listening done on another device since.
   */
  private serverPosition: { itemId: string; at: number; seenAt: number } | null = null;
  /** A resumed book checking that no other device moved it while paused; syncs wait for it. */
  private resumeCheck: Promise<void> | null = null;
  /**
   * End-of-chapter sleep: `follow` = the chapter under the playhead (re-aimed
   * after seeks); otherwise the chapter at `index` (or the one under the
   * playhead once playback has moved past it).
   */
  private sleepTarget: { follow: boolean; index: number | null } = { follow: true, index: null };
  /** The loaded book as offline listening sessions report it (local and direct-file playback have no server session). */
  private localBook: LocalSessionBook | null = null;
  /** A downloaded copy failed while playing: the reload of this book streams instead (ENG-003). */
  private streamOnlyItem: string | null = null;
  /** Downloads this browser could not open (format): streamed while online for the rest of the visit. */
  private localUnplayable = new Set<string>();
  /** True while a failed download is taken out of use and the book reloads from the server. */
  private recoveringLocal = false;
  /** The current play attempt's startup trace (DATA-021). */
  private trace: PlaybackStartupTrace | null = null;
  /** The background load `preload()` started, until it ends or another load replaces it. */
  private preloading: Preload | null = null;
  /**
   * A Cast receiver playing the book instead of this device (see `remote.ts`).
   * The element stays loaded and paused at the book, so playback can come back here.
   */
  private remote: RemotePlayback | null = null;
  private remoteTimer: ReturnType<typeof setInterval> | null = null;
  /** The receiver's last sampled position, for listening time and periodic syncs. */
  private remoteSample: { at: number; wall: number; seeks: number } | null = null;

  constructor() {
    const last = storage.read<LastPlayed | null>(LAST_PLAYED_KEY, null);
    const session = getSession();
    if (last && session && last.serverUrl === session.serverUrl) {
      this.patch({
        itemId: last.itemId,
        title: last.title,
        author: last.author,
        currentTime: last.currentTime,
        duration: last.duration,
        chapters: restoredChapters(last.chapters),
        coverUrl: coverUrl(last.itemId, 600),
        // The speed the book will play at (its "This Book" speed, else the global one), as load() sets it.
        rate: this.rateFor(last.itemId),
        status: 'ready',
      });
    }
    progressStore.subscribe(() => void this.showResumePosition());
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        // Let a save still in flight (e.g. the pause that just happened offline)
        // land first, so the newest position is the one that reaches the server.
        void this.syncChain.then(() => flushPendingProgress()).then(() => flushLocalListening());
        this.onBackOnline();
      });
      // Listening time a previous visit collected offline.
      setTimeout(() => void flushLocalListening().catch(() => 0), 0);
      window.addEventListener('pagehide', () => this.flushOnExit());
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          this.interruption.hidden(Date.now());
          this.flushOnExit();
          return;
        }
        // Refresh an expiring token on return so a tap on play can use it right away.
        if (this.state.source === 'stream') void ensureFreshToken(5 * 60_000).catch(() => undefined);
        // Back from a call that took the screen (iOS returns to the app when it ends).
        this.resumeAfterInterruption('visible');
      });
      this.watchInterruptions();
      setInterval(() => this.checkStall(), WATCHDOG_INTERVAL_MS);
      this.setupMediaSession();
    }
  }

  get state(): PlayerState {
    return this.store.get();
  }

  private patch(input: Partial<PlayerState>) {
    let p = input;
    // `error` (the message) and `errorInfo` always change together.
    if (p.errorInfo !== undefined) p = { ...p, error: p.errorInfo?.message ?? null };
    else if (p.error !== undefined) p = { ...p, errorInfo: p.error == null ? null : playerError('unknown', p.error) };
    const prev = this.store.get();
    const pausePosition = p.isPlaying === false && this.playingItemId ? (p.finished && p.currentTime != null ? p.currentTime : this.currentTime()) : 0;
    this.store.set((s) => ({ ...s, ...p }));
    this.emitTransitions(prev, this.store.get(), p, pausePosition);
  }

  /** Derives play/pause/buffering/error/chapter events from state changes. Emission only. */
  private emitTransitions(prev: PlayerState, next: PlayerState, p: Partial<PlayerState>, pausePosition: number) {
    if (p.isPlaying === true && !this.playingItemId && next.itemId && this.hasRealSource()) {
      this.playingItemId = next.itemId;
      this.events.emit('play', { itemId: next.itemId, position: this.currentTime() });
    } else if (p.isPlaying === false && this.playingItemId) {
      const itemId = this.playingItemId;
      this.playingItemId = null;
      const stopped =
        this.stopping || !!p.finished || p.status === 'loading' || p.status === 'error' || p.loaded === false || this.endedLastFile();
      this.events.emit('pause', { itemId, position: pausePosition, stopped });
    }
    if (p.isBuffering !== undefined && prev.isBuffering !== next.isBuffering) {
      const itemId = next.isBuffering ? next.itemId : (prev.itemId ?? next.itemId);
      if (itemId) this.events.emit(next.isBuffering ? 'bufferingStart' : 'bufferingEnd', { itemId, position: next.currentTime });
    }
    if (p.error && p.error !== prev.error) {
      const info = next.errorInfo;
      this.events.emit('error', {
        itemId: next.itemId,
        message: p.error,
        fatal: next.status === 'error',
        code: info?.code ?? 'unknown',
        action: info?.action ?? 'retry',
      });
    }
    if (p.currentTime !== undefined || p.chapters !== undefined || p.itemId !== undefined || p.loaded !== undefined) {
      const index = chapterIndexAt(next.chapters, next.currentTime);
      const track = this.chapterTrack;
      if (!prev.loaded || !next.loaded || track.itemId !== next.itemId || track.chapters !== next.chapters) {
        this.chapterTrack = { itemId: next.itemId, chapters: next.chapters, index };
      } else if (index !== track.index && next.itemId) {
        const from = track.index;
        track.index = index;
        this.events.emit('chapterChanged', { itemId: next.itemId, from, to: index });
      }
    }
  }

  private hasRealSource(): boolean {
    const a = this.audio;
    return !!a && !!a.src && a.src !== this.unlockUrl;
  }

  /** The element reached the end of the book (not just the end of one file). */
  private endedLastFile(): boolean {
    const a = this.audio;
    const track = this.tracks[this.trackIndex];
    return !!a?.ended && (!!track?.isHls || this.trackIndex >= this.tracks.length - 1);
  }

  // ------------------------------------------------------------ UI accessors

  /** The last play attempts' startup traces, oldest first (Flutter `playbackStartupHistory`, diagnostics). */
  startupHistory(): PlaybackStartupSnapshot[] {
    return playbackStartupHistory();
  }

  /** The (lazily created) audio element, for Web Audio effects. */
  getAudioElement(): HTMLAudioElement {
    return this.el();
  }

  /** Buffered spans of the current file, in seconds from the start of the book. */
  bufferedRanges(): Array<{ start: number; end: number }> {
    const a = this.audio;
    const track = this.tracks[this.trackIndex];
    if (!a || !track || !this.state.loaded || !this.hasRealSource()) return [];
    const offset = track.isHls ? 0 : track.startOffset;
    const out: Array<{ start: number; end: number }> = [];
    try {
      for (let i = 0; i < a.buffered.length; i++) out.push({ start: a.buffered.start(i) + offset, end: a.buffered.end(i) + offset });
    } catch {
      /* ranges change while reading */
    }
    return out;
  }

  /** End of the buffered span under the playhead (Flutter's `bufferedPosition`); the playhead itself when nothing is buffered. */
  bufferedPosition(): number {
    const t = this.currentTime();
    for (const r of this.bufferedRanges()) {
      if (t >= r.start - 0.5 && t <= r.end + 0.5) return Math.max(t, r.end);
    }
    return t;
  }

  /**
   * Caps the element volume (0..1). Audio boost uses it to attenuate when the
   * Web Audio graph is not attached; the sleep fade scales from this level.
   */
  setOutputVolume(volume: number) {
    this.outputVolume = Math.min(1, Math.max(0, Number.isFinite(volume) ? volume : 1));
    if (this.audio) this.audio.volume = this.outputVolume;
  }

  // ------------------------------------------------------------ element

  private el(): HTMLAudioElement {
    if (this.audio) return this.audio;
    const a = document.createElement('audio');
    a.preload = 'auto';
    a.setAttribute('playsinline', '');
    a.setAttribute('webkit-playsinline', '');
    a.crossOrigin = null;
    a.style.display = 'none';
    if (this.outputVolume !== 1) a.volume = this.outputVolume;
    document.body.appendChild(a);

    a.addEventListener('loadedmetadata', () => {
      const track = this.tracks[this.trackIndex];
      if (track && a.src && a.src !== this.unlockUrl) this.decodedTracks.add(track.index);
      if (this.pendingSeek != null) {
        try {
          a.currentTime = this.pendingSeek;
        } catch {
          /* some browsers need canplay first */
        }
      }
    });
    a.addEventListener('canplay', () => {
      if (this.pendingSeek != null && Math.abs(a.currentTime - this.pendingSeek) > 1) {
        a.currentTime = this.pendingSeek;
      }
      this.pendingSeek = null;
      this.switchingTrack = false;
      // While casting, the receiver's status drives the play state, not the idle element here.
      if (this.remote) return;
      this.patch({ isBuffering: false });
      if (this.hasRealSource()) this.traceReady();
    });
    a.addEventListener('timeupdate', () => {
      if (!this.remote) this.onTick();
    });
    a.addEventListener('playing', () => {
      if (this.remote) {
        // Nothing plays here while casting (a late play() from before the cast began).
        a.pause();
        return;
      }
      if (this.hasRealSource()) this.trace?.mark('T5.player_playing');
      this.lastTickAt = performance.now();
      this.lastProgressAt = this.lastTickAt;
      this.patch({ isPlaying: true, isBuffering: false, status: 'ready', error: null });
      this.updatePositionState();
      if ('mediaSession' in navigator) navigator.mediaSession.playbackState = 'playing';
    });
    a.addEventListener('play', () => {
      if (this.remote) return;
      // The browser resumed the book after an interruption (Chrome after a call, WebKit when iOS allows it).
      if (!this.wantPlaying && this.hasRealSource()) this.adoptBrowserResume();
      this.patch({ isPlaying: true });
    });
    a.addEventListener('pause', () => {
      if (this.remote) return;
      // 'pause' also fires at the end of each file of a multi-file book.
      if (this.switchingTrack || (a.ended && this.trackIndex < this.tracks.length - 1)) return;
      // The element was emptied (teardown, before a new book): teardown already saved progress.
      if (!a.src || a.src === this.unlockUrl) return;
      // Paused by the system (a call, another app taking audio): don't let the
      // stall watchdog or a recovery start it again behind the user's back.
      // A failed load pauses too; the error handler resumes that one.
      // The interruption is remembered, so the book can carry on once it is over.
      if (!a.error && this.wantPlaying && !this.stopping && this.state.loaded && this.state.itemId) {
        this.interruption.begin(this.state.itemId, Date.now(), document.visibilityState !== 'hidden');
      }
      if (!a.error) this.wantPlaying = false;
      this.pausedAt = Date.now();
      // A failed load the error handler is about to retry stays "buffering", so the
      // play button shows the reconnection instead of a paused player.
      this.patch({ isPlaying: false, isBuffering: !!a.error && this.wantPlaying });
      this.updatePositionState();
      if ('mediaSession' in navigator) navigator.mediaSession.playbackState = 'paused';
      // At the very end, finishBook() does the final (finished) sync.
      if (!a.ended) void this.sync({ force: true }).then(() => this.endLocalListening());
    });
    a.addEventListener('waiting', () => {
      if (this.remote) return;
      if (this.hasRealSource()) this.trace?.mark('T5.player_buffering');
      this.patch({ isBuffering: true });
    });
    // Safari fires 'stalled' on paused or fully buffered media; only trust it while playing.
    a.addEventListener('stalled', () => {
      if (!this.remote && !a.paused && a.readyState < HTMLMediaElement.HAVE_FUTURE_DATA) this.patch({ isBuffering: true });
    });
    a.addEventListener('canplaythrough', () => {
      if (!this.remote) this.patch({ isBuffering: false });
    });
    a.addEventListener('ratechange', () => {
      this.patch({ rate: a.playbackRate });
      this.updatePositionState();
    });
    a.addEventListener('ended', () => {
      if (!this.remote) void this.onEnded();
    });
    // A failure of the idle element while casting is dealt with when playback comes back (play() reloads it).
    a.addEventListener('error', () => {
      if (!this.remote) void this.onError();
    });
    this.audio = a;
    return a;
  }

  /**
   * Must be called synchronously inside a tap handler before any `await`.
   * iOS only lets an element start audio later if it was started from a gesture.
   */
  unlock() {
    const a = this.el();
    if (this.state.loaded || !a.paused) return;
    this.unlockUrl ??= silentWavUrl();
    a.src = this.unlockUrl;
    a.play().then(() => {
      if (a.src === this.unlockUrl) a.pause();
    }).catch(() => undefined);
  }

  // ------------------------------------------------------------ loading

  /**
   * Opens the restored book paused, in the background, so a later Play starts
   * at once: the session (or local file), the resume position and the source
   * are ready, and the browser can buffer ahead where it allows (iOS waits for
   * the tap, which then plays with no network round trip before it). Nothing
   * changes on screen: the play button stays Play, and a failure is silent (the
   * tap loads the book as before). A tap while it runs joins it instead of
   * starting over. Does nothing when no book is restored, or one is loaded or
   * loading.
   */
  preload(): void {
    const s = this.state;
    if (!s.itemId || s.loaded || s.status !== 'ready' || this.preloading || s.isPlaying) return;
    void this.load(s.itemId, { autoplay: false, preload: true });
  }

  async load(itemId: string, opts: LoadOptions & { preload?: boolean } = {}): Promise<void> {
    const autoplay = opts.preload ? false : (opts.autoplay ?? true);
    if (autoplay) this.unlock();
    // The book is already opening in the background at its resume position: join that load.
    if (!opts.preload && opts.startAt === undefined && this.joinsPreload(itemId)) {
      if (autoplay) await this.play();
      return;
    }
    const token = ++this.loadToken;
    const preload: Preload | null = opts.preload ? { token, itemId, play: false } : null;
    this.preloading = preload;
    const serialAtStart = this.seekSerial;
    this.trace?.finish('cancelled', 'superseded by another load');
    const trace = new PlaybackStartupTrace(itemId, this.recoveringLocal ? 'localRecovery' : preload ? 'preload' : autoplay ? 'play' : 'open');
    this.trace = trace;
    await this.teardown();
    if (token !== this.loadToken) return;
    // Another book while casting: the TV stops the old one now, not once the new one is ready.
    if (this.remote?.itemId && this.remote.itemId !== itemId) this.remote.stop();
    const sameItem = itemId === this.state.itemId;
    if (!sameItem) {
      this.forceTranscode = false;
      if (this.unloadedSeek?.itemId !== itemId) this.unloadedSeek = null;
      // An end-of-chapter timer aimed at a chapter of the previous book follows the new book's playhead.
      this.sleepTarget = { follow: true, index: null };
    }

    const md = opts.preview?.media.metadata;
    const fromPreview = previewChapters(opts.preview);
    const picked = this.pickedStart(itemId, opts.startAt, serialAtStart);
    const sleep = this.state.sleep;
    this.patch({
      ...INITIAL,
      // A preload keeps the restored book's Play button (see `preload`).
      status: preload ? 'ready' : 'loading',
      itemId,
      title: md?.title ?? (sameItem ? this.state.title : ''),
      author: md?.authorName ?? (sameItem ? this.state.author : ''),
      coverUrl: coverUrl(itemId, 600),
      duration: opts.preview?.media.duration ?? (sameItem ? this.state.duration : 0),
      // Chapters and position stay on screen while the session opens (restored book, preview, retry).
      chapters: fromPreview.length > 0 ? fromPreview : sameItem ? this.state.chapters : [],
      currentTime: picked ?? (sameItem ? this.state.currentTime : 0),
      rate: this.rateFor(itemId),
      sleep: !sameItem && sleep.mode === 'chapter' ? { mode: 'chapter', endsAt: null } : sleep,
    });
    trace.mark('T1.ui_buffering');
    const streamOnly = this.streamOnlyItem === itemId || (navigator.onLine !== false && this.localUnplayable.has(itemId));
    this.streamOnlyItem = null;

    try {
      const localProgress = await trace.preflight('localProgress', () => getLocalProgress(itemId));
      const local = streamOnly ? null : await trace.preflight('localFile', () => openLocalPlayback(itemId));
      if (token !== this.loadToken) {
        local?.revoke();
        trace.finish('cancelled');
        return;
      }

      let startAt: number;
      // The server's progress as last known (fresh, else a snapshot), for `serverPosition`.
      let known: Pick<MediaProgress, 'currentTime' | 'isFinished' | 'lastUpdate'> | undefined;
      if (local) {
        this.local = local;
        const r = local.record;
        this.tracks = r.tracks.map((t, i) => ({
          index: t.index,
          startOffset: t.startOffset,
          duration: t.duration,
          url: local.urls[i]!,
          mimeType: t.mimeType,
        }));
        this.trackPaths = [];
        let server: Awaited<ReturnType<typeof getProgress>> | undefined;
        if (navigator.onLine !== false) {
          server = await trace.preflight('serverProgress', () => getProgress(itemId)).catch(() => undefined);
          if (token !== this.loadToken) return trace.finish('cancelled');
        }
        // Without a fresh answer (offline, request failed) the last known server progress
        // stands in, so a book never starts at 0:00 and then rewinds the server.
        server ??= progressStore.get()[itemId] ?? r.item?.userMediaProgress;
        known = server;
        startAt = this.pickedStart(itemId, opts.startAt, serialAtStart, () => pickResumePosition(server, localProgress));
        this.localBook = { itemId, title: r.title, author: r.author, duration: r.duration, libraryId: r.libraryId ?? r.item?.libraryId ?? null, bookId: r.item?.media?.id ?? null };
        trace.sourceReady('local');
        this.patch({
          source: 'local',
          title: r.title,
          author: r.author,
          duration: r.duration,
          chapters: r.chapters.length ? r.chapters : chaptersFromTracks(r.tracks),
          coverUrl: navigator.onLine === false && local.coverUrl ? local.coverUrl : coverUrl(itemId, 600),
        });
      } else {
        if (navigator.onLine === false) {
          throw new PlayerFailure('offline', "You're offline and this book isn't downloaded.");
        }
        // Fetch the server's progress alongside the session so we can compare
        // timestamps with any position saved on this device.
        const [opened, server] = await Promise.all([
          this.openStreamOrDirect(itemId, trace),
          localProgress ? trace.preflight('serverProgress', () => getProgress(itemId)).catch(() => undefined) : Promise.resolve(undefined),
        ]);
        if (token !== this.loadToken) {
          if (opened.session) void closeSession(opened.session.id, opened.session.currentTime, 0, opened.session.duration).catch(() => undefined);
          trace.finish('cancelled');
          return;
        }
        const { session, tracks, paths } = opened;
        this.session = session;
        this.tracks = tracks;
        this.trackPaths = paths;
        if (session) {
          // The session's position is the server's; without its timestamp, the last known one stands in.
          const fromServer = server ?? { currentTime: session.currentTime, lastUpdate: progressStore.get()[itemId]?.lastUpdate ?? 0, isFinished: false };
          known = fromServer;
          startAt = this.pickedStart(itemId, opts.startAt, serialAtStart, () =>
            localProgress ? pickResumePosition(fromServer, localProgress) : session.currentTime,
          );
          const smd = session.mediaMetadata;
          this.localBook = null;
          trace.sourceReady('stream', tracks[0]?.url);
          this.patch({
            source: 'stream',
            title: session.displayTitle ?? smd?.title ?? this.state.title,
            author: session.displayAuthor ?? smd?.authorName ?? this.state.author,
            duration: session.duration,
            chapters: session.chapters?.length ? session.chapters : chaptersFromTracks(this.tracks),
          });
        } else {
          // Direct files without a session (ENG-005): progress goes through PATCH, listening time as a local session.
          const item = opened.item!;
          const md = item.media.metadata;
          const duration = item.media.duration || tracks.reduce((n, t) => n + t.duration, 0);
          known = server ?? opened.progress;
          startAt = this.pickedStart(itemId, opts.startAt, serialAtStart, () => pickResumePosition(known, localProgress));
          const title = md.title ?? this.state.title;
          const author = md.authorName ?? md.authors?.map((a) => a.name).join(', ') ?? this.state.author;
          this.localBook = { itemId, title, author, duration, libraryId: item.libraryId ?? null, bookId: item.media.id ?? null };
          trace.sourceReady('direct', tracks[0]?.url);
          this.patch({
            source: 'stream',
            title,
            author,
            duration,
            chapters: item.media.chapters?.length ? item.media.chapters : chaptersFromTracks(tracks),
          });
        }
      }

      const duration = this.state.duration || this.tracks.reduce((n, t) => n + t.duration, 0);
      if (startAt >= duration - 5) startAt = 0; // finished books start over
      this.patch({ duration, currentTime: startAt, status: 'ready', loaded: true });
      this.serverPosition = known ? { itemId, at: known.isFinished ? 0 : known.currentTime, seenAt: Date.now() } : null;
      this.events.emit('sessionStarted', { itemId, source: this.state.source ?? 'stream', startAt, duration, title: this.state.title });
      this.rememberLastPlayed();
      this.updateMetadata();

      const { trackIndex, offset } = locateTrack(this.tracks, startAt);
      await this.attachTrack(trackIndex, offset);
      if (token !== this.loadToken) return trace.finish('cancelled');
      // The picked position has been applied; later seeks go to the element.
      if (this.unloadedSeek?.itemId === itemId) this.unloadedSeek = null;
      if (this.state.sleep.mode === 'chapter') this.refreshSleepTarget(this.sleepIndexAt(startAt));
      const a = this.el();
      a.playbackRate = this.state.rate;
      a.defaultPlaybackRate = this.state.rate;
      if (this.preloading === preload) this.preloading = null;
      if (this.remote) await this.sendToRemote(autoplay || !!preload?.play);
      else if (autoplay || preload?.play) await this.play();
    } catch (err) {
      if (this.preloading === preload) this.preloading = null;
      if (token !== this.loadToken) return trace.finish('cancelled');
      const errorInfo = classifyPlayerError(err);
      trace.finish('error', errorInfo.message);
      if (preload && !preload.play) {
        // Nobody asked to play yet: keep the restored book as it was; a tap loads it again and reports any error.
        console.warn('[player] preload failed', err);
        this.patch({ status: 'ready', isPlaying: false, isBuffering: false, loaded: false });
        return;
      }
      console.error('[player] load failed', err);
      if (preload) this.wantPlaying = false;
      this.patch({ status: 'error', errorInfo, isPlaying: false, isBuffering: false, loaded: false });
    }
  }

  /** A paused load is ready once the element can play; a playing one once it reports a position. */
  private traceReady() {
    const trace = this.trace;
    if (!trace) return;
    trace.mark('T5.player_ready');
    if (!this.wantPlaying && !this.state.isPlaying) trace.finish('success', 'ready (paused)');
  }

  /**
   * Opens the ABS playback session; when that fails for a reason other than
   * auth or a missing book while online, falls back to the item's audio
   * files without a session (Flutter `_resolveDirectSource`, ENG-005). The
   * session's own error is the one reported when the fallback fails too.
   */
  private async openStreamOrDirect(
    itemId: string,
    trace: PlaybackStartupTrace,
  ): Promise<{ session: PlaybackSession | null; tracks: PlayerTrack[]; paths: string[]; item?: LibraryItem; progress?: LibraryItem['userMediaProgress'] }> {
    try {
      return await trace.preflight('session', () => this.openStreamSession(itemId));
    } catch (err) {
      if (!canFallBackToDirect(err)) throw err;
      console.warn('[player] playback session failed; playing the files directly', err);
      try {
        return { session: null, ...(await trace.preflight('directSource', () => this.openDirectSource(itemId))) };
      } catch (fallbackError) {
        console.warn('[player] direct-file fallback failed', fallbackError);
        throw err;
      }
    }
  }

  /** The item's audio files as tracks with `?token=` URLs (no session: nothing transcodes). */
  private async openDirectSource(itemId: string): Promise<{ tracks: PlayerTrack[]; paths: string[]; item: LibraryItem; progress?: LibraryItem['userMediaProgress'] }> {
    const item = await getItem(itemId);
    const files = (item.media.audioFiles ?? []).filter((f) => !f.exclude && !f.invalid).sort((a, b) => a.index - b.index);
    if (files.length === 0) throw new PlayerFailure('notFound', 'This item has no playable audio.');
    let offset = 0;
    const paths: string[] = [];
    const tracks = files.map((f) => {
      const path = audioFilePath(itemId, f.ino);
      paths.push(path);
      const track: PlayerTrack = { index: f.index, startOffset: offset, duration: f.duration, url: mediaUrl(path), mimeType: f.mimeType ?? 'audio/mpeg' };
      offset += f.duration;
      return track;
    });
    return { tracks, paths, item, progress: item.userMediaProgress ?? item.mediaProgress };
  }

  private async openStreamSession(itemId: string): Promise<{ session: PlaybackSession; tracks: PlayerTrack[]; paths: string[] }> {
    const mimes = supportedMimeTypes(this.el());
    const session = await startPlaybackSession(itemId, mimes, this.forceTranscode);
    const tracks = [...(session.audioTracks ?? [])].sort((a, b) => a.index - b.index);
    if (tracks.length === 0) throw new PlayerFailure('notFound', 'This item has no playable audio.');
    const isHls = session.playMethod === PlayMethod.Transcode || tracks.some((t) => /\.m3u8(\?|$)/.test(t.contentUrl));
    return {
      session,
      paths: tracks.map((t) => t.contentUrl),
      tracks: tracks.map((t) => ({
        index: t.index,
        startOffset: t.startOffset,
        duration: t.duration,
        url: isHls ? resolveServerPath(getSession()!.serverUrl, t.contentUrl) : mediaUrl(t.contentUrl),
        mimeType: t.mimeType,
        isHls,
      })),
    };
  }

  private async attachTrack(index: number, offset: number) {
    const a = this.el();
    const track = this.tracks[index];
    if (!track) throw new Error('Track not found');
    this.trackIndex = index;
    this.pendingSeek = offset > 0.5 ? offset : null;
    this.lastProgressAt = performance.now();
    this.trace?.mark('T4.set_audio_source_called');
    if (track.isHls) {
      await this.attachHls(track, offset);
      return;
    }
    this.destroyHls();
    (a as HTMLAudioElement & { disableRemotePlayback?: boolean }).disableRemotePlayback = false;
    a.src = this.trackUrl(index);
    a.load();
  }

  /**
   * Stream URLs carry the access token, so they're rebuilt from the server path
   * each time a file is attached: a book that runs longer than the token's
   * lifetime keeps playing into its next file.
   */
  private trackUrl(index: number): string {
    const track = this.tracks[index]!;
    const path = this.trackPaths[index];
    if (this.state.source !== 'stream' || track.isHls || !path) return track.url;
    return mediaUrl(path) || track.url;
  }

  private async attachHls(track: PlayerTrack, offset: number) {
    const a = this.el();
    // Safari plays HLS natively, which also keeps audio alive on the iOS lock
    // screen more reliably than Media Source. ABS's HLS routes need no auth.
    if (a.canPlayType('application/vnd.apple.mpegurl')) {
      this.destroyHls();
      a.src = track.url;
      a.load();
      return;
    }
    const { default: Hls } = await import('hls.js/light');
    if (!Hls.isSupported()) throw new PlayerFailure('unsupportedFormat', 'This browser can’t play this audio format.');
    this.destroyHls();
    // WebKit's ManagedMediaSource (used by hls.js on iOS 17.1+) requires this.
    (a as HTMLAudioElement & { disableRemotePlayback?: boolean }).disableRemotePlayback = true;
    const hls = new Hls({
      startPosition: offset,
      maxBufferLength: HLS_FORWARD_BUFFER_SECONDS,
      maxMaxBufferLength: HLS_FORWARD_BUFFER_SECONDS,
      backBufferLength: HLS_BACK_BUFFER_SECONDS,
      xhrSetup: (xhr) => {
        const s = getSession();
        if (s) xhr.setRequestHeader('Authorization', `Bearer ${s.accessToken}`);
      },
    });
    hls.on(Hls.Events.ERROR, (_e, data) => {
      if (!data.fatal || this.hls !== hls) return;
      if (data.type === Hls.ErrorTypes.MEDIA_ERROR && this.mediaErrorRecoveries++ < 2) {
        hls.recoverMediaError();
        return;
      }
      // hls.js has already retried the request itself. The transcode may have
      // died with its session (server restart, idle cleanup), so reopen one.
      void this.recover('error');
    });
    hls.loadSource(track.url);
    hls.attachMedia(a);
    // pendingSeek stays set until 'canplay': until hls.js reaches startPosition
    // the element reports 0, which must never be saved as the listening position.
    this.hls = hls;
  }

  private destroyHls() {
    if (this.hls) {
      this.hls.destroy();
      this.hls = null;
    }
  }

  // ------------------------------------------------------------ transport

  /** A `preload()` of `itemId` is still running. */
  private joinsPreload(itemId: string): boolean {
    const pre = this.preloading;
    return !!pre && pre.token === this.loadToken && pre.itemId === itemId;
  }

  async play(): Promise<void> {
    const s = this.state;
    if (!s.itemId) return;
    this.interruption.clear();
    const pre = this.preloading;
    if (pre && this.joinsPreload(s.itemId)) {
      // The book is opening in the background: join that load (it plays once attached) instead of starting over.
      pre.play = true;
      this.wantPlaying = true;
      if (!s.loaded) {
        this.patch({ status: 'loading', error: null });
        return;
      }
    }
    if (this.remote && s.loaded && s.status !== 'error') return this.playRemote();
    // Not loaded yet, or the element gave up after an error: (re)load where we were.
    if (!s.loaded || s.status === 'error' || this.audio?.error) {
      await this.load(s.itemId, { autoplay: true, startAt: s.loaded ? this.currentTime() : undefined });
      return;
    }
    const a = this.el();
    // A timer that ran out while paused, whose expiry a background tab delayed, must not stop this playback.
    if (s.sleep.mode === 'timer' && s.sleep.endsAt != null && s.sleep.endsAt <= Date.now()) this.cancelSleepTimer('expired');
    // Asked before smart rewind moves the playhead.
    const recheck = a.paused && this.atServerPosition() && Date.now() - this.serverPosition!.seenAt > RESUME_CHECK_AFTER_MS;
    this.wantPlaying = true;
    if (settingsStore.get().smartRewind && this.pausedAt) {
      const back = smartRewindSeconds(Date.now() - this.pausedAt);
      if (back > 0) {
        this.nextSeek = { kind: 'smartRewind', chapterIndex: -1 };
        this.seek(Math.max(0, this.currentTime() - back));
      }
    }
    this.pausedAt = 0;
    if (recheck) this.checkResume(s.itemId);
    this.swapExpiredStreamUrl();
    this.lastProgressAt = performance.now();
    this.releaseLockScreen();
    try {
      await a.play();
    } catch (err) {
      const name = (err as DOMException)?.name;
      // A new source replaced this one (next file, recovery, seek to another
      // file): the listener still wants to hear it. A source the browser
      // rejects is handled by the 'error' event (stream: retries; download:
      // continues from the server, or reports the error offline).
      if (name === 'AbortError' || (name === 'NotSupportedError' && this.state.source !== null)) return;
      this.wantPlaying = false;
      if (name === 'NotAllowedError') {
        this.patch({ isPlaying: false, isBuffering: false, error: null });
        return; // needs a tap; the play button is right there
      }
      throw err;
    }
  }

  /** The paused playhead is where the server has it, as far as this device knows, and no listening time waits to be sent: there is nothing to save. */
  private atServerPosition(position = this.currentTime()): boolean {
    const known = this.serverPosition;
    if (!known || known.itemId !== this.state.itemId || this.wantPlaying || !(this.audio?.paused ?? true)) return false;
    return Math.abs(position - known.at) < 0.5 && Math.round(this.listenedSinceSync) < 1;
  }

  /**
   * Resuming a book left paused here: another device may have moved it on
   * meanwhile, and this device's first sync would rewind that. Playback starts
   * right away (an await here could cost the tap's permission to play) and
   * moves to the server's position if it changed; syncs wait for the answer.
   */
  private checkResume(itemId: string) {
    const known = this.serverPosition!;
    const seeksAtStart = this.seekCount;
    const timeout = new Promise<undefined>((r) => setTimeout(r, RESUME_CHECK_TIMEOUT_MS));
    const check = (async () => {
      if (navigator.onLine === false) return;
      const server = await Promise.race([getProgress(itemId), timeout]).catch(() => undefined);
      // Moved on here meanwhile (another book, a sync): nothing to correct.
      if (!server || server.isFinished || this.state.itemId !== itemId || !this.state.loaded || this.serverPosition !== known) return;
      if (Math.abs(server.currentTime - known.at) < 1) {
        known.seenAt = Date.now();
        return;
      }
      this.serverPosition = { itemId, at: server.currentTime, seenAt: Date.now() };
      // A seek made here meanwhile is the listener's choice and stands.
      if (this.seekCount !== seeksAtStart) return;
      this.nextSeek = { kind: 'serverSync', chapterIndex: -1 };
      this.seek(server.currentTime);
    })();
    const tracked: Promise<void> = check.finally(() => {
      if (this.resumeCheck === tracked) this.resumeCheck = null;
    });
    this.resumeCheck = tracked;
  }

  pause() {
    if (this.onRemote()) return this.pauseRemote();
    this.wantPlaying = false;
    this.interruption.clear();
    const a = this.audio;
    // An element that already stopped (error, mid-recovery) fires no 'pause' event.
    if (a?.paused) this.patch({ isPlaying: false, isBuffering: false });
    a?.pause();
    if (this.state.loaded && this.state.itemId) this.holdLockScreen();
  }

  toggle() {
    if (this.state.isPlaying) this.pause();
    else {
      this.unlock();
      void this.play();
    }
  }

  /** Lock-screen "stop": saves progress, releases the stream and clears the media notification. */
  async stop(): Promise<void> {
    const position = this.currentTime();
    const itemId = this.state.itemId;
    this.stopping = true;
    try {
      this.pause();
      // Cancels any load or stream recovery still in flight.
      this.loadToken++;
      await this.teardown();
      // The receiver stays connected, idle, for the next book.
      this.remote?.stop();
      this.patch({ isPlaying: false, loaded: false, currentTime: position, isBuffering: false });
    } finally {
      this.stopping = false;
    }
    if ('mediaSession' in navigator) {
      navigator.mediaSession.playbackState = 'none';
      navigator.mediaSession.metadata = null;
    }
    this.events.emit('stop', { itemId, position });
  }

  /** Stop and forget the current book entirely (hides the mini player). */
  async close(): Promise<void> {
    await this.stop();
    this.clearSleepExpiry();
    this.unloadedSeek = null;
    this.sleepTarget = { follow: true, index: null };
    this.localUnplayable.clear();
    storage.write(LAST_PLAYED_KEY, null);
    this.patch({ ...INITIAL });
  }

  currentTime(): number {
    // While casting, the idle element here is where casting began: never report that as the position.
    if (this.remote) return this.onRemote() ? this.remote.position() : this.state.currentTime;
    const a = this.audio;
    const track = this.tracks[this.trackIndex];
    if (!a || !track || !this.state.loaded) return this.state.currentTime;
    if (this.pendingSeek != null) return track.startOffset + this.pendingSeek;
    return track.isHls ? a.currentTime : track.startOffset + a.currentTime;
  }

  seek(time: number) {
    const s = this.state;
    const from = this.currentTime();
    // While the duration is unknown (a book still loading) the load clamps the position itself.
    const target = Math.max(0, s.duration > 0 ? Math.min(time, s.duration - 0.5) : time);
    const described = this.nextSeek;
    this.nextSeek = null;
    this.seekCount++;
    this.sleepChapterEnd = null; // re-evaluated for the chapter we land in
    this.patch({ currentTime: target, finished: false });
    if (s.sleep.mode === 'chapter') this.refreshSleepTarget(this.sleepIndexAt(target));
    if (s.itemId) {
      const chapterIndex = described && described.chapterIndex >= 0 ? described.chapterIndex : chapterIndexAt(s.chapters, target);
      this.events.emit('seek', {
        itemId: s.itemId,
        from,
        to: target,
        kind: described?.kind ?? 'seek',
        chapterIndex,
        chapter: s.chapters[chapterIndex] ?? null,
      });
    }
    if (!s.loaded) {
      // A restored (or still loading) book: its next load starts here.
      if (s.itemId) this.rememberUnloadedSeek(s.itemId, target);
      return;
    }
    if (this.onRemote()) {
      this.remote!.seek(target);
      this.remoteSample = null;
      this.updatePositionState();
      return;
    }
    const a = this.el();
    const { trackIndex, offset } = locateTrack(this.tracks, target);
    const track = this.tracks[trackIndex];
    if (track?.isHls) {
      if (a.readyState >= 1) a.currentTime = target;
      else this.pendingSeek = target;
    } else if (trackIndex === this.trackIndex) {
      if (a.readyState >= 1) a.currentTime = offset;
      else this.pendingSeek = offset;
    } else {
      const wasPlaying = !a.paused || this.wantPlaying;
      this.switchingTrack = true;
      void this.attachTrack(trackIndex, offset).then(() => {
        if (wasPlaying) void a.play().catch(() => undefined);
      });
    }
    this.updatePositionState();
  }

  /**
   * Where the next load of `itemId` should start: a position picked while it
   * wasn't loaded beats the saved progress (`resume`), and beats `explicit` when
   * picked after the load began. A skip picked then moves from `explicit`, else
   * from `resume`, rather than from the position the restored book showed.
   */
  private pickedStart(itemId: string, explicit: number | undefined, serialAtStart: number): number | undefined;
  private pickedStart(itemId: string, explicit: number | undefined, serialAtStart: number, resume: () => number): number;
  private pickedStart(itemId: string, explicit: number | undefined, serialAtStart: number, resume?: () => number): number | undefined {
    const picked = this.unloadedSeek?.itemId === itemId ? this.unloadedSeek : null;
    if (picked && (explicit === undefined || picked.serial > serialAtStart)) {
      if (picked.delta === undefined || !resume) return picked.at;
      return Math.max(0, (explicit ?? resume()) + picked.delta);
    }
    return explicit ?? resume?.();
  }

  private rememberUnloadedSeek(itemId: string, at: number, delta?: number) {
    this.unloadedSeek = { itemId, at, serial: ++this.seekSerial, delta };
    // Keep the restored display where the listener left it across a reload.
    if (this.state.status !== 'loading') this.rememberLastPlayed();
  }

  skip(delta: number) {
    if (delta !== 0) this.nextSeek = { kind: delta < 0 ? 'skipBack' : 'skipForward', chapterIndex: -1 };
    const itemId = this.state.itemId;
    const prior = this.unloadedSeek?.itemId === itemId ? this.unloadedSeek : null;
    this.seek(this.currentTime() + delta);
    // Not loaded: the load moves by the skips from wherever the book resumes. Skips after a picked position move from that position.
    const picked = this.unloadedSeek;
    if (!this.state.loaded && picked && picked !== prior && picked.itemId === itemId && (!prior || prior.delta !== undefined)) {
      picked.delta = (prior?.delta ?? 0) + delta;
    }
  }

  /**
   * `opts.transient` marks intermediate values while a slider is dragged, so
   * history records only the settled speed (it is applied and saved either way).
   */
  setRate(rate: number, scope: 'book' | 'all' = 'book', opts: { transient?: boolean } = {}) {
    const from = this.state.rate;
    const r = normalizePlaybackSpeed(rate);
    if (this.audio) {
      this.audio.playbackRate = r;
      this.audio.defaultPlaybackRate = r;
    }
    if (this.onRemote()) this.remote!.setRate(r);
    this.patch({ rate: r });
    const itemId = this.state.itemId;
    const settings = settingsStore.get();
    if (scope === 'all') updateSettings({ defaultPlaybackRate: r, bookRates: {} });
    else if (itemId) updateSettings({ bookRates: { ...settings.bookRates, [itemId]: r } });
    if (r !== from || !opts.transient) this.events.emit('rateChanged', { itemId, from, to: r, scope, transient: !!opts.transient });
  }

  /**
   * Applies a playback rate without saving it anywhere; callers that follow
   * Flutter's speed scopes (`player/speed.ts`) persist it themselves.
   * `silent` marks a change the listener didn't make as a speed choice (the
   * scope toggle), which history does not record.
   */
  applyRate(rate: number, opts: { scope?: 'book' | 'all'; transient?: boolean; silent?: boolean } = {}) {
    const from = this.state.rate;
    const r = normalizePlaybackSpeed(rate);
    if (this.audio) {
      this.audio.playbackRate = r;
      this.audio.defaultPlaybackRate = r;
    }
    if (this.onRemote()) this.remote!.setRate(r);
    this.patch({ rate: r });
    if (r !== from || !opts.transient) {
      this.events.emit('rateChanged', {
        itemId: this.state.itemId,
        from,
        to: r,
        scope: opts.scope ?? 'book',
        transient: !!opts.transient,
        ...(opts.silent ? { silent: true } : {}),
      });
    }
  }

  /**
   * Speed a book starts at (Flutter `_syncPlaybackSpeedForBook`): its scope
   * ("This Book" = its saved speed or 1x, "All Books" = the global speed),
   * normalised so values saved by older versions land on the 0.05 grid.
   */
  private rateFor(itemId: string): number {
    const s = settingsStore.get();
    const override = s.bookRates?.[itemId];
    if (getBookSpeedScope(itemId, s) === 'thisBook') return normalizePlaybackSpeed(override ?? 1);
    return normalizePlaybackSpeed(s.defaultPlaybackRate);
  }

  currentChapterIndex(time = this.state.currentTime): number {
    return chapterIndexAt(this.state.chapters, time);
  }

  /**
   * Flutter `skipToNextChapter`: the next chapter's start; on the last chapter
   * the end of the book (a plain seek); without chapters a forward skip.
   */
  nextChapter() {
    const chapters = this.state.chapters;
    if (chapters.length === 0) return this.skip(settingsStore.get().skipForwardSeconds);
    const i = this.currentChapterIndex(this.currentTime());
    const next = chapters[i + 1];
    if (!next) return this.seek(this.state.duration);
    this.nextSeek = { kind: 'chapterNext', chapterIndex: i + 1 };
    this.seek(next.start + CHAPTER_LANDING_OFFSET);
  }

  /**
   * Flutter `skipToPreviousChapter`: restart the chapter when more than 3 s
   * in, else the previous chapter, else the start of the book (a plain seek);
   * without chapters a backward skip.
   */
  previousChapter() {
    const chapters = this.state.chapters;
    if (chapters.length === 0) return this.skip(-settingsStore.get().skipBackSeconds);
    const t = this.currentTime();
    const i = this.currentChapterIndex(t);
    const current = chapters[i];
    const previous = chapters[i - 1];
    if (current && t - current.start > 3) {
      this.nextSeek = { kind: 'chapterPrevious', chapterIndex: i };
      this.seek(current.start + CHAPTER_LANDING_OFFSET);
    } else if (previous) {
      this.nextSeek = { kind: 'chapterPrevious', chapterIndex: i - 1 };
      this.seek(previous.start + CHAPTER_LANDING_OFFSET);
    } else {
      this.seek(0);
    }
  }

  /** Jumps to the start of chapter `index` (chapter list taps). Does not start playback. */
  seekToChapter(index: number) {
    const chapter = this.state.chapters[index];
    if (!chapter) return;
    this.nextSeek = { kind: 'chapterSelect', chapterIndex: index };
    this.seek(chapter.start + CHAPTER_LANDING_OFFSET);
  }

  // ------------------------------------------------------------ sleep timer

  setSleepTimer(minutes: number) {
    this.restoreVolume();
    const endsAt = Date.now() + minutes * 60000;
    this.patch({ sleep: { mode: 'timer', endsAt } });
    this.armSleepExpiry(endsAt);
    this.events.emit('sleepTimerSet', { itemId: this.state.itemId, mode: 'timer', durationMs: minutes * 60000, endsAt });
  }

  /** End of the chapter the end-of-chapter sleep timer is waiting for. */
  private sleepChapterEnd: number | null = null;
  /** Wall-clock expiry of a countdown timer (it runs while paused too, as in Flutter). */
  private sleepExpiry: ReturnType<typeof setTimeout> | null = null;

  private armSleepExpiry(endsAt: number) {
    this.clearSleepExpiry();
    // setTimeout delays are capped at ~24.8 days; a longer timer re-arms when this one fires.
    this.sleepExpiry = setTimeout(() => this.onSleepExpiry(endsAt), Math.min(MAX_TIMEOUT_MS, Math.max(0, endsAt - Date.now())));
  }

  private clearSleepExpiry() {
    if (this.sleepExpiry != null) clearTimeout(this.sleepExpiry);
    this.sleepExpiry = null;
  }

  /** The countdown ran out: pause if playing (fade included), else just record the expiry now. */
  private onSleepExpiry(endsAt: number) {
    this.sleepExpiry = null;
    const { sleep, isPlaying } = this.state;
    if (sleep.mode !== 'timer' || sleep.endsAt !== endsAt) return;
    if (Date.now() < endsAt - 50) return this.armSleepExpiry(endsAt);
    if (this.wantPlaying || isPlaying) this.triggerSleep();
    else {
      // Ran out during a call: the listener meant to stop by now.
      this.interruption.clear();
      this.cancelSleepTimer('expired');
    }
  }

  /**
   * Pauses at the end of a chapter (position-based: follows seeks and speed,
   * idle while paused). Without `index`: the chapter under the playhead, re-aimed
   * when a seek lands in another chapter ("End of Chapter"). With `index`: that
   * chapter ("End of Next Chapter", "End of Selected Chapter"); if playback moves
   * past it, the chapter under the playhead takes over. Returns false (and
   * changes nothing) when that chapter is behind the playhead or out of range.
   */
  sleepAtEndOfChapter(index?: number): boolean {
    const chapters = this.state.chapters;
    const t = this.currentTime();
    const current = chapterIndexAt(chapters, t);
    if (index !== undefined) {
      const chapter = chapters[index];
      if (!chapter || index < current || chapter.end <= t) return false;
    }
    this.sleepChapterEnd = null;
    this.clearSleepExpiry();
    this.restoreVolume();
    this.sleepTarget = index === undefined ? { follow: true, index: null } : { follow: false, index };
    const target = index ?? current;
    const chapter = chapters[target];
    this.patch({ sleep: { mode: 'chapter', endsAt: null, chapterIndex: chapter ? target : null, chapterEnd: chapter?.end ?? null } });
    const durationMs = chapter ? Math.max(0, Math.round((chapter.end - t) * 1000)) : 0;
    this.events.emit('sleepTimerSet', {
      itemId: this.state.itemId,
      mode: 'chapter',
      durationMs,
      endsAt: null,
      ...(chapter ? { chapterIndex: target } : {}),
    });
    return true;
  }

  /** The chapter whose end pauses playback for a playhead at `t` (end-of-chapter mode); retargets a passed fixed chapter. */
  private sleepIndexAt(t: number): number {
    const current = chapterIndexAt(this.state.chapters, t);
    const target = this.sleepTarget;
    if (target.follow || target.index == null) return current;
    if (current > target.index) this.sleepTarget = { follow: false, index: current };
    return Math.max(current, target.index);
  }

  /** Publishes the end-of-chapter target (`sleep.chapterIndex` / `chapterEnd`) when it changed. */
  private refreshSleepTarget(index: number) {
    const { sleep, chapters } = this.state;
    if (sleep.mode !== 'chapter') return;
    const chapter = chapters[index];
    const chapterIndex = chapter ? index : null;
    const chapterEnd = chapter?.end ?? null;
    if (sleep.chapterIndex === chapterIndex && sleep.chapterEnd === chapterEnd) return;
    this.patch({ sleep: { mode: 'chapter', endsAt: null, chapterIndex, chapterEnd } });
  }

  extendSleepTimer(minutes: number) {
    const { sleep } = this.state;
    const now = Date.now();
    const base = sleep.mode === 'timer' && sleep.endsAt ? Math.max(sleep.endsAt, now) : now;
    this.restoreVolume();
    const endsAt = base + minutes * 60000;
    this.patch({ sleep: { mode: 'timer', endsAt } });
    this.armSleepExpiry(endsAt);
    this.events.emit('sleepTimerSet', { itemId: this.state.itemId, mode: 'timer', durationMs: endsAt - now, endsAt });
  }

  cancelSleepTimer(reason: SleepCancelReason = 'user') {
    const wasActive = this.state.sleep.mode !== 'off';
    this.clearSleepExpiry();
    this.restoreVolume();
    this.patch({ sleep: { mode: 'off', endsAt: null } });
    if (wasActive) this.events.emit('sleepTimerCancelled', { itemId: this.state.itemId, reason });
  }

  private restoreVolume() {
    if (this.audio) this.audio.volume = this.outputVolume;
  }

  private triggerSleep() {
    this.clearSleepExpiry();
    this.events.emit('sleepTimerFired', { itemId: this.state.itemId });
    this.pause();
    // Nobody is listening: let iOS put the app away as usual.
    this.releaseLockScreen();
    this.restoreVolume();
    this.patch({ sleep: { mode: 'off', endsAt: null } });
    if (settingsStore.get().notifySleepTimer) {
      void notify('Sleep timer ended', { body: `Paused ${this.state.title}.`, tag: 'sleep', url: '/player' });
    }
  }

  private checkSleep(t: number) {
    const { sleep, chapters } = this.state;
    if (sleep.mode === 'timer' && sleep.endsAt) {
      const remaining = (sleep.endsAt - Date.now()) / 1000;
      if (remaining <= 0) return this.triggerSleep();
      if (settingsStore.get().sleepTimerFadeOut && this.audio && remaining < SLEEP_FADE_SECONDS) {
        this.audio.volume = Math.max(0.05, remaining / SLEEP_FADE_SECONDS) * this.outputVolume;
      }
    } else if (sleep.mode === 'chapter') {
      // timeupdate fires every ~250ms of wall time, which at 2x+ can step over
      // a narrow window at the boundary; remember the target chapter's end
      // instead and stop once playback crosses it.
      const end = this.sleepChapterEnd;
      if (end != null && t >= end - 0.3 && t < end + 5) return this.triggerSleep();
      const index = this.sleepIndexAt(t);
      this.sleepChapterEnd = chapters[index]?.end ?? null;
      this.refreshSleepTarget(index);
    }
  }

  // ------------------------------------------------------------ ticking & sync

  private onTick() {
    const a = this.audio;
    if (!a || this.switchingTrack || this.pendingSeek != null) return;
    const now = performance.now();
    const t = this.currentTime();
    if (this.trace && !this.trace.finished && !a.paused && this.hasRealSource()) {
      this.trace.mark('T6.first_position');
      this.trace.finish('success');
    }
    if (Math.abs(t - this.lastProgressPos) > 0.05) {
      this.lastProgressPos = t;
      this.lastProgressAt = now;
    }
    if (!a.paused && this.lastTickAt) {
      const delta = (now - this.lastTickAt) / 1000;
      if (delta > 0 && delta < 5) this.listenedSinceSync += delta;
    }
    this.lastTickAt = now;
    if (!a.paused) this.checkSleep(t);
    if (now - this.lastStoreUpdate > 250) {
      this.lastStoreUpdate = now;
      this.patch({ currentTime: t });
    }
    // Keep the lock-screen chapter title and scrubber accurate.
    if (this.currentChapterIndex(t) !== this.lastChapterForMetadata || now - this.lastPositionState > 10000) {
      this.lastPositionState = now;
      this.updatePositionState();
    }
    const wall = Date.now();
    if (wall - this.lastLocalSave > LOCAL_SAVE_INTERVAL_MS) {
      this.lastLocalSave = wall;
      this.rememberLastPlayed();
    }
    if (!a.paused && wall - this.lastServerSync > SERVER_SYNC_INTERVAL_MS) {
      void this.sync();
    }
    if (!a.paused && this.errorRetries > 0 && wall - this.lastRecoveryAt > 30000) {
      // Playing steadily again since the last recovery.
      this.errorRetries = 0;
      this.mediaErrorRecoveries = 0;
    }
    if (!a.paused && this.state.source === 'stream' && wall - this.lastTokenCheck > 60000) {
      // Keep the token fresh while listening so the next file, and any recovery, use a valid one.
      this.lastTokenCheck = wall;
      void ensureFreshToken(5 * 60_000).catch(() => undefined);
    }
  }

  private syncChain: Promise<void> = Promise.resolve();
  private syncsInFlight = 0;

  /**
   * Saves progress locally and to ABS (or queues it when offline).
   * Syncs run one after another, so a forced sync (pause, finish, switching
   * books) is never dropped because a periodic one is still in flight.
   */
  sync(opts: { force?: boolean; finished?: boolean } = {}): Promise<void> {
    const s = this.state;
    if (!s.itemId || !s.loaded) return Promise.resolve();
    if (!opts.finished) {
      // A resumed book may be about to move to where another device left it.
      if (this.resumeCheck) return opts.force ? this.resumeCheck.then(() => this.sync(opts)) : Promise.resolve();
      // Paused where the server has it: saving would only rewind listening done elsewhere since.
      if (this.atServerPosition()) return Promise.resolve();
    }
    if (!opts.force && !opts.finished && (this.syncsInFlight > 0 || Date.now() - this.lastServerSync < 2000)) {
      return Promise.resolve();
    }
    this.lastServerSync = Date.now();
    const listened = Math.round(this.listenedSinceSync);
    this.listenedSinceSync -= listened;
    const job = {
      itemId: s.itemId,
      currentTime: opts.finished ? s.duration : this.currentTime(),
      duration: s.duration,
      sessionId: this.session?.id ?? null,
      listened,
      finished: !!opts.finished,
      stamp: Date.now(),
    };
    this.rememberLastPlayed();
    this.syncsInFlight++;
    this.syncChain = this.syncChain
      .then(() => this.runSync(job))
      .catch(() => undefined)
      .finally(() => {
        this.syncsInFlight--;
      });
    return this.syncChain;
  }

  private async runSync(job: { itemId: string; currentTime: number; duration: number; sessionId: string | null; listened: number; finished: boolean; stamp: number }) {
    const { itemId, currentTime, duration, finished, stamp } = job;
    patchProgress(itemId, { currentTime, duration, progress: finished ? 1 : duration ? currentTime / duration : 0, ...(finished ? { isFinished: true } : {}) });
    // No server session (downloaded book, direct files): the listening time is kept for a local session.
    if (!job.sessionId && job.listened > 0) await this.recordListening(itemId, job.listened, currentTime);
    // The server request doesn't wait for the local save: iOS may suspend the page
    // seconds after a background pause, and IndexedDB can stall there (seen on iOS).
    const savedLocally = Promise.race([
      saveLocalProgress({ itemId, currentTime, duration, isFinished: finished, pendingSync: true, lastUpdate: stamp }),
      new Promise((r) => setTimeout(r, 2000)),
    ]);
    if (navigator.onLine === false) {
      await savedLocally;
      return;
    }
    try {
      if (job.sessionId && !finished) {
        try {
          await syncSession(job.sessionId, currentTime, job.listened, duration);
        } catch (err) {
          // ABS forgets sessions after a server restart; fall back to plain progress.
          if (err instanceof AbsError && err.status === 404) {
            if (this.session?.id === job.sessionId) {
              this.session = null;
              this.localBook ??= { itemId, title: this.state.title, author: this.state.author, duration };
            }
            await this.recordListening(itemId, job.listened, currentTime);
            job.listened = 0;
            await updateProgress(itemId, { currentTime, duration, progress: duration ? currentTime / duration : 0 });
          } else throw err;
        }
      } else {
        await updateProgress(itemId, {
          currentTime,
          duration,
          progress: finished ? 1 : duration ? currentTime / duration : 0,
          isFinished: finished,
        });
      }
      if (this.state.itemId === itemId) this.serverPosition = { itemId, at: currentTime, seenAt: Date.now() };
    } catch (err) {
      // Session time goes again with the next sync (local-session time is already saved).
      if (job.sessionId && this.state.itemId === itemId) this.listenedSinceSync += job.listened;
      if (!(err instanceof AbsError && err.isNetworkError)) console.warn('[player] sync failed', err);
      await savedLocally;
      return;
    }
    await savedLocally;
    await markSynced(itemId, stamp);
  }

  /** Adds listening without a server session to this book's local session (uploaded later). */
  private async recordListening(itemId: string, listened: number, currentTime: number) {
    const account = getSession();
    if (!account || !(listened > 0)) return;
    const book = this.localBook?.itemId === itemId ? this.localBook : { itemId, title: this.state.title, author: this.state.author, duration: this.state.duration };
    await Promise.race([recordLocalListening(account, book, listened, currentTime), new Promise((r) => setTimeout(r, 2000))]);
  }

  /** Playback paused, stopped or moved on: the local session ends and goes to the server when online. */
  private endLocalListening() {
    closeLocalListening();
    if (navigator.onLine !== false) void flushLocalListening().catch(() => 0);
  }

  /**
   * A restored or stopped book shows where its next load resumes (the server's
   * position, unless one saved here still waits to sync) rather than where this
   * device left it, which another device may have moved past since.
   */
  private async showResumePosition() {
    const idle = (itemId: string | null) => {
      const s = this.state;
      return !!itemId && s.itemId === itemId && !s.loaded && s.status !== 'loading' && this.unloadedSeek?.itemId !== itemId;
    };
    const { itemId } = this.state;
    const server = itemId ? progressStore.get()[itemId] : undefined;
    if (!idle(itemId) || !server) return;
    const local = await getLocalProgress(itemId!);
    if (!idle(itemId)) return;
    const at = pickResumePosition(server, local);
    if (Math.abs(at - this.state.currentTime) < 1) return;
    this.patch({ currentTime: at });
    this.rememberLastPlayed();
  }

  private lastExitFlush = 0;

  private flushOnExit() {
    const s = this.state;
    if (!s.itemId || !s.loaded) return;
    // Nothing new to save (see `sync`).
    if (this.resumeCheck || this.atServerPosition()) return;
    // pagehide and visibilitychange often fire together.
    if (Date.now() - this.lastExitFlush < 2000) return;
    this.lastExitFlush = Date.now();
    const currentTime = this.currentTime();
    void saveLocalProgress({ itemId: s.itemId, currentTime, duration: s.duration, isFinished: false, pendingSync: true });
    this.rememberLastPlayed();
    if (!this.session && this.listenedSinceSync >= 1) {
      // Best effort: the page may be frozen before IndexedDB finishes.
      const listened = Math.round(this.listenedSinceSync);
      this.listenedSinceSync -= listened;
      void this.recordListening(s.itemId, listened, currentTime);
    }
    const session = getSession();
    if (!session || navigator.onLine === false) return;
    // keepalive lets the request finish after the page is frozen or closed.
    const body = this.session
      ? { url: `/api/session/${this.session.id}/sync`, method: 'POST', data: { currentTime, timeListened: Math.round(this.listenedSinceSync), duration: s.duration } }
      : { url: `/api/me/progress/${s.itemId}`, method: 'PATCH', data: { currentTime, duration: s.duration, progress: s.duration ? currentTime / s.duration : 0 } };
    this.listenedSinceSync = 0;
    try {
      void fetch(resolveServerPath(session.serverUrl, body.url), {
        method: body.method,
        keepalive: true,
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.accessToken}` },
        body: JSON.stringify(body.data),
      }).catch(() => undefined);
    } catch {
      /* ignore */
    }
  }

  private rememberLastPlayed() {
    const s = this.state;
    const session = getSession();
    if (!s.itemId || !session) return;
    storage.write(LAST_PLAYED_KEY, {
      itemId: s.itemId,
      title: s.title,
      author: s.author,
      currentTime: this.currentTime(),
      duration: s.duration,
      serverUrl: session.serverUrl,
      chapters: this.lastPlayedChapters(s.chapters),
    } satisfies LastPlayed);
  }

  private storedChapters: { source: readonly Chapter[]; stored: Chapter[] } | null = null;

  /** The chapters to store with LastPlayed (converted once per chapter list). */
  private lastPlayedChapters(chapters: readonly Chapter[]): Chapter[] {
    if (this.storedChapters?.source !== chapters) this.storedChapters = { source: chapters, stored: storableChapters(chapters) };
    return this.storedChapters.stored;
  }

  private async onEnded() {
    if (this.tracks[this.trackIndex]?.isHls || this.trackIndex >= this.tracks.length - 1) {
      await this.finishBook();
      return;
    }
    // "Sleep at end of chapter" where the chapter ends with this file: timeupdate
    // may never land in the last fraction of a second, so stop here.
    const { sleep, chapters } = this.state;
    const fileEnd = this.tracks[this.trackIndex]!.startOffset + this.tracks[this.trackIndex]!.duration;
    if (sleep.mode === 'chapter') {
      const ch = chapters[this.sleepIndexAt(fileEnd - 1)];
      if (!ch || Math.abs(ch.end - fileEnd) < 1.5) {
        await this.attachTrack(this.trackIndex + 1, 0);
        this.triggerSleep();
        // The element is already stopped, so no 'pause' event will do this for us.
        this.pausedAt = Date.now();
        this.patch({ currentTime: fileEnd, isPlaying: false });
        if ('mediaSession' in navigator) navigator.mediaSession.playbackState = 'paused';
        void this.sync({ force: true });
        return;
      }
    }
    // Swap to the next file synchronously in the 'ended' handler: iOS keeps
    // background playback alive only if the next play() happens right away.
    const a = this.el();
    this.trackIndex += 1;
    this.switchingTrack = true;
    this.pendingSeek = null;
    this.lastProgressAt = performance.now();
    a.src = this.trackUrl(this.trackIndex);
    a.play().catch(() => undefined);
  }

  private async finishBook() {
    const s = this.state;
    this.wantPlaying = false;
    this.interruption.clear();
    this.patch({ isPlaying: false, currentTime: s.duration, finished: true });
    if (s.itemId) this.events.emit('bookFinished', { itemId: s.itemId, duration: s.duration });
    if (s.sleep.mode === 'chapter') this.cancelSleepTimer('finished');
    await this.sync({ finished: true });
    this.endLocalListening();
    if (this.session) {
      const id = this.session.id;
      this.session = null;
      void closeSession(id, s.duration, 0, s.duration).catch(() => undefined);
    }
    if (settingsStore.get().notifyFinished) {
      void notify('Finished!', { body: `You finished ${s.title}.`, tag: `finished-${s.itemId}`, url: `/item/${s.itemId}` });
    }
  }

  private async onError() {
    const a = this.audio;
    if (!a || !a.src || a.src === this.unlockUrl) return;
    const code = a.error?.code;
    this.switchingTrack = false;
    if (this.state.source === 'local') {
      // The failed copy is being replaced by the stream; its leftover events don't count.
      if (this.recoveringLocal) return;
      if (await this.recoverFromLocalFailure(code)) return;
    }
    if (this.state.source === 'stream') {
      if (navigator.onLine === false) {
        // Keep the book loaded; the 'online' handler picks up where we were.
        this.patch({
          isBuffering: this.wantPlaying,
          errorInfo: playerError('offline', "You're offline. Playback resumes when you're back online.", { fatal: false }),
        });
        return;
      }
      if (this.errorRetries < MAX_RECOVERIES) {
        // A file the browser can't decode won't get better by retrying, so
        // after two plain retries ask ABS to transcode it instead. Chrome also
        // reports HTTP errors (401, 503) as "not supported", so only blame the
        // format for a file that never loaded while the server is serving it.
        const src = a.src;
        const track = this.tracks[this.trackIndex];
        if (this.errorRetries >= 2 && !this.forceTranscode && track && !track.isHls && !this.decodedTracks.has(track.index)) {
          const token = this.loadToken;
          const undecodable =
            code === MediaError.MEDIA_ERR_DECODE || (code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED && (await serverServes(src)));
          if (token !== this.loadToken) return;
          if (undecodable) this.forceTranscode = true;
        }
        await this.recover('error');
        return;
      }
    }
    this.fail(mediaFailure(this.state.source, code, navigator.onLine !== false));
  }

  private fail(error: PlayerError) {
    this.wantPlaying = false;
    this.interruption.clear();
    this.trace?.finish('error', error.message);
    this.patch({ status: 'error', errorInfo: { ...error, fatal: true }, isPlaying: false, isBuffering: false });
  }

  /**
   * A downloaded copy failed while playing (ENG-003, Flutter runtime recovery,
   * player_provider.dart:1337-1395): while online and signed in, the book
   * continues from the server at the same position. A file the browser cannot
   * decode is taken out of use (its download fails with "Tap to download
   * again" and the corrupt audio is deleted); a format it does not support
   * stays downloaded and this one playback streams, transcoded by ABS.
   * Returns false when recovery does not apply (offline, signed out, already
   * recovering), and the caller reports the error.
   */
  private async recoverFromLocalFailure(code: number | undefined): Promise<boolean> {
    const s = this.state;
    const itemId = s.itemId;
    if (!itemId || this.recoveringLocal || navigator.onLine === false || !getSession()) return false;
    const token = this.loadToken;
    const position = this.currentTime();
    const resume = this.wantPlaying || s.isPlaying;
    const track = this.tracks[this.trackIndex];
    // "Not supported" from a file that never opened is the format; anything else is the stored copy.
    const unsupportedFormat = code === MEDIA_ERR_SRC_NOT_SUPPORTED && !!track && !this.decodedTracks.has(track.index);
    this.recoveringLocal = true;
    console.warn(`[player] downloaded copy failed (code ${code ?? '?'}); continuing from the server`);
    try {
      // Save the position and listening time first, then let go of the stored files.
      await this.sync({ force: true }).catch(() => undefined);
      if (token !== this.loadToken) return true;
      this.patch({ currentTime: position, loaded: false, isBuffering: true, error: null });
      const a = this.audio;
      if (a) {
        a.removeAttribute('src');
        a.load();
      }
      this.local?.revoke();
      this.local = null;
      this.streamOnlyItem = itemId;
      if (unsupportedFormat) {
        this.forceTranscode = true;
        this.localUnplayable.add(itemId);
      } else if (code === MEDIA_ERR_DECODE) {
        // Undecodable bytes: the stored copy is damaged, so it is downloaded again.
        await quarantineDownload(itemId, { discardFiles: true });
      } else {
        // Network/abort errors and a lost file URL (iOS invalidates them after a suspend) say
        // nothing about the stored bytes; stream for now and keep the download.
        this.localUnplayable.add(itemId);
      }
      if (token !== this.loadToken) return true;
      await this.load(itemId, { startAt: position, autoplay: resume });
    } finally {
      this.recoveringLocal = false;
    }
    return true;
  }

  /**
   * Gets a broken or stalled stream going again at the same position:
   * refreshes the token, rebuilds the URLs and, for transcoded (HLS) books or
   * repeated failures, opens a new ABS playback session, since a transcode
   * dies with its session (server restart, idle cleanup).
   */
  private async recover(reason: 'error' | 'stall' | 'online'): Promise<void> {
    const s = this.state;
    if (this.remote || this.recovering || s.source !== 'stream' || !s.loaded || !s.itemId || this.tracks.length === 0) return;
    if (this.errorRetries >= MAX_RECOVERIES) {
      this.fail(playerError('streamDropped', 'The stream keeps dropping. Check your connection and try again.'));
      return;
    }
    const attempt = ++this.errorRetries;
    const token = this.loadToken;
    const itemId = s.itemId;
    const position = this.currentTime();
    this.recovering = true;
    this.lastRecoveryAt = Date.now();
    this.patch({ isBuffering: this.wantPlaying });
    console.warn(`[player] recovering stream (${reason}, attempt ${attempt})`);
    let failed = false;
    try {
      if (attempt > 1) await new Promise((r) => setTimeout(r, Math.min(8000, 1000 * (attempt - 1))));
      if (token !== this.loadToken) return;
      // After an error the token may have been revoked or expired early; otherwise only refresh if it's due.
      if (reason === 'error') await refreshAccessToken();
      else await ensureFreshToken();
      if (token !== this.loadToken) return;
      if (this.forceTranscode || attempt >= 3 || this.tracks.some((t) => t.isHls)) {
        const previous = this.session;
        const opened = await this.openStreamSession(itemId);
        if (token !== this.loadToken) {
          void closeSession(opened.session.id, position, 0, opened.session.duration).catch(() => undefined);
          return;
        }
        this.session = opened.session;
        this.tracks = opened.tracks;
        this.trackPaths = opened.paths;
        if (previous && previous.id !== opened.session.id) void closeSession(previous.id, position, 0, s.duration).catch(() => undefined);
      }
      const { trackIndex, offset } = locateTrack(this.tracks, position);
      this.switchingTrack = true;
      await this.attachTrack(trackIndex, offset);
      if (token !== this.loadToken) return;
      // Read the intent now, not when recovery began: the listener (or a
      // phone call) may have paused while we were reconnecting.
      if (this.wantPlaying) {
        // Not awaited: if this load fails too, its 'error' event must find the recovery finished.
        this.el().play().catch(() => undefined);
      }
    } catch (err) {
      console.warn('[player] recovery failed', err);
      if (token === this.loadToken) {
        this.switchingTrack = false;
        failed = true;
      }
    } finally {
      if (token === this.loadToken) {
        this.recovering = false;
        // Give the reloaded stream a full stall window before judging it.
        this.lastProgressAt = performance.now();
        if (!this.wantPlaying) this.patch({ isBuffering: false });
      }
    }
    // Reconnecting itself failed (offline, server restarting, 5xx): nothing
    // else will fire on the old, dead element, so try again with the remaining
    // attempts (recover() backs off and gives up with a message). While the
    // device is offline the 'online' handler takes over instead.
    if (failed && this.wantPlaying && token === this.loadToken && navigator.onLine !== false) void this.recover(reason);
  }

  /** Watchdog: a stream that should be playing but hasn't moved for a while is reloaded. */
  private checkStall() {
    const a = this.audio;
    const s = this.state;
    if (!a || this.remote || !this.wantPlaying || this.recovering || !s.loaded || s.source !== 'stream' || s.status === 'error') return;
    if (navigator.onLine === false || (a.paused && !this.switchingTrack)) return;
    if (performance.now() - this.lastProgressAt < STALL_TIMEOUT_MS) return;
    void this.recover('stall');
  }

  /** Network is back: restart a stream that stalled or failed while we were offline. */
  private onBackOnline() {
    const s = this.state;
    if (this.remote || !this.wantPlaying || !s.loaded || s.source !== 'stream' || this.recovering) return;
    const a = this.audio;
    if (s.isBuffering || a?.error || (a && a.readyState < HTMLMediaElement.HAVE_FUTURE_DATA)) {
      this.errorRetries = 0;
      void this.recover('online');
    }
  }

  /**
   * Resuming after a long pause: if the token in the current stream URL has
   * expired but a fresh one is available, switch URLs now (still inside the
   * tap, so iOS allows playback) instead of waiting for the next range
   * request to fail with a 401.
   */
  private swapExpiredStreamUrl() {
    const a = this.audio;
    const track = this.tracks[this.trackIndex];
    if (!a || !track || track.isHls || this.state.source !== 'stream' || !a.src) return;
    let current: string | null = null;
    try {
      current = new URL(a.src).searchParams.get('token');
    } catch {
      return;
    }
    const exp = tokenExpiresAt(current ?? undefined);
    if (exp == null || exp - Date.now() > 60_000) return;
    const fresh = this.trackUrl(this.trackIndex);
    if (fresh === a.src) return;
    const offset = this.pendingSeek ?? a.currentTime;
    this.pendingSeek = offset > 0.5 ? offset : null;
    this.switchingTrack = true;
    a.src = fresh;
    a.load();
  }

  private async teardown() {
    this.releaseLockScreen();
    this.interruption.clear();
    this.sleepChapterEnd = null;
    const s = this.state;
    const settled = s.loaded && !this.resumeCheck && this.atServerPosition();
    if (s.loaded && s.itemId) {
      await this.sync({ force: true }).catch(() => undefined);
    }
    if (this.localBook) {
      this.endLocalListening();
      this.localBook = null;
    }
    if (this.session) {
      const { id } = this.session;
      const t = this.currentTime();
      const itemId = s.itemId;
      this.session = null;
      // Closing saves a position too, and the one this device last synced may be behind another device's by
      // now: close at the server's own. Unanswered, the session stays open until this device opens its next.
      const at = settled && itemId ? getProgress(itemId).then((p) => p.currentTime) : Promise.resolve(t);
      void at.then((pos) => closeSession(id, pos, 0, s.duration)).catch(() => undefined);
    }
    this.destroyHls();
    if (this.audio) {
      this.audio.pause();
      this.audio.removeAttribute('src');
      this.audio.load();
    }
    this.local?.revoke();
    this.local = null;
    this.tracks = [];
    this.trackPaths = [];
    this.trackIndex = 0;
    this.pendingSeek = null;
    this.listenedSinceSync = 0;
    this.errorRetries = 0;
    this.mediaErrorRecoveries = 0;
    this.decodedTracks.clear();
    this.recovering = false;
    this.switchingTrack = false;
    this.pausedAt = 0;
  }

  // ------------------------------------------------------------ interruptions (ENG-019)

  /** Signals that an interruption ended (Audio Session API) or that the output changed (headphones, Bluetooth). */
  private watchInterruptions() {
    const session = audioSessionOf(navigator);
    if (session) {
      let last = session.state;
      if (last === 'interrupted') this.markInterrupted();
      session.addEventListener('statechange', () => {
        const state = session.state;
        if (state === 'interrupted') this.markInterrupted();
        else if (last === 'interrupted') this.resumeAfterInterruption('audioSession');
        last = state;
      });
    }
    try {
      navigator.mediaDevices?.addEventListener?.('devicechange', () => this.interruption.routeChanged(Date.now()));
    } catch {
      /* no media devices (insecure context, old browser) */
    }
  }

  /** The Audio Session API says an interruption began: remember it even if the element's pause comes later or not at all. */
  private markInterrupted() {
    const s = this.state;
    // A call on the phone doesn't concern the TV playing the book.
    if (this.remote) return;
    if (!this.wantPlaying || !s.loaded || !s.itemId || this.stopping) return;
    this.interruption.begin(s.itemId, Date.now(), document.visibilityState !== 'hidden');
  }

  /** Plays again if `signal` means the interruption of the loaded book is over. */
  private resumeAfterInterruption(signal: 'audioSession' | 'visible') {
    const s = this.state;
    // A call on the phone doesn't concern the TV playing the book.
    if (this.remote) return;
    if (!this.interruption.pending(s.itemId, Date.now())) return;
    // Not ours to resume: a load, an error, or playback the browser already restarted.
    if (!s.loaded || s.status !== 'ready' || this.wantPlaying || !(this.audio?.paused ?? true)) return;
    if (!this.interruption.shouldResume(signal, s.itemId, Date.now())) return;
    // A sleep timer that ran out during the interruption ends the listening (play() would only cancel it).
    if (s.sleep.mode === 'timer' && s.sleep.endsAt != null && s.sleep.endsAt <= Date.now()) {
      this.cancelSleepTimer('expired');
      return;
    }
    // A refusal (no gesture) leaves the book paused, with its play button.
    void this.play().catch(() => undefined);
  }

  /**
   * The browser played the element again by itself after a pause we did not
   * ask for: after an interruption, the listener's wish to hear the book is
   * back, so recovery and the stall watchdog look after it again. Smart rewind
   * applies as for a resume from the app.
   */
  private adoptBrowserResume() {
    const s = this.state;
    if (!this.interruption.shouldResume('browser', s.itemId, Date.now())) return;
    this.wantPlaying = true;
    if (settingsStore.get().smartRewind && this.pausedAt) {
      const back = smartRewindSeconds(Date.now() - this.pausedAt);
      if (back > 0) {
        this.nextSeek = { kind: 'smartRewind', chapterIndex: -1 };
        this.seek(Math.max(0, this.currentTime() - back));
      }
    }
    this.pausedAt = 0;
    this.lastProgressAt = performance.now();
    this.releaseLockScreen();
  }

  // ------------------------------------------------------------ casting

  /** The receiver plays (or is loading) the loaded book: transport and position go to it. */
  private onRemote(): boolean {
    const r = this.remote;
    return !!r && this.state.loaded && r.itemId != null && r.itemId === this.state.itemId;
  }

  /** The device this book plays on instead of this one, or null. */
  remoteDevice(): string | null {
    return this.remote?.deviceName ?? null;
  }

  /**
   * A Cast receiver connected: the book moves to it at the same position, and
   * keeps playing if it was. A receiver that already plays a book (a session
   * resumed after a reload) is followed instead: that book loads here, paused,
   * and the receiver carries on. The element here stays loaded and paused.
   */
  async connectRemote(remote: RemotePlayback): Promise<void> {
    if (this.remote === remote) return;
    if (this.remote) this.disconnectRemote(this.remote);
    const s = this.state;
    const wasPlaying = s.loaded && (this.wantPlaying || s.isPlaying);
    const at = this.currentTime();
    this.remote = remote;
    this.remoteSample = null;
    this.releaseLockScreen();
    this.audio?.pause();
    this.remoteTimer ??= setInterval(() => this.remoteTick(), 1000);
    if (remote.itemId && remote.hasMedia() && remote.itemId !== s.itemId) {
      // The receiver plays another book: follow it (load() hands over to it when done).
      await this.load(remote.itemId, { autoplay: false });
      return;
    }
    if (!s.itemId || s.status === 'loading') return; // a load in flight hands its book to the receiver itself
    if (!s.loaded) {
      await this.load(s.itemId, { autoplay: false });
      return;
    }
    this.patch({ currentTime: at });
    await this.sendToRemote(wasPlaying, at);
  }

  /**
   * The receiver went away (the listener stopped casting, the TV turned off or
   * dropped off the network): playback comes back here at the receiver's
   * position, and keeps playing if it was playing there.
   */
  disconnectRemote(remote: RemotePlayback): void {
    if (this.remote !== remote) return;
    const on = this.onRemote();
    const position = on ? remote.position() : null;
    const resume = on && remote.isPlaying();
    this.remote = null;
    this.remoteSample = null;
    if (this.remoteTimer) clearInterval(this.remoteTimer);
    this.remoteTimer = null;
    if (position == null || !this.state.loaded) {
      this.wantPlaying = false;
      if (this.state.isPlaying || this.state.isBuffering) this.patch({ isPlaying: false, isBuffering: false });
      return;
    }
    this.patch({ currentTime: position, error: null });
    this.placeElement(position);
    if (this.tracks.some((t) => t.isHls) && this.state.itemId) {
      // A transcode left idle while casting may have lost its stream: reopen it at the TV's position.
      void this.load(this.state.itemId, { autoplay: resume, startAt: position });
      return;
    }
    if (resume) {
      this.pausedAt = 0;
      void this.play().catch(() => this.patch({ isPlaying: false, isBuffering: false }));
    } else {
      this.wantPlaying = false;
      this.pausedAt = Date.now();
      this.patch({ isPlaying: false, isBuffering: false });
      void this.sync({ force: true });
    }
  }

  /** The receiver's playback changed (its own controls, a TV remote, Google Home, buffering, errors). */
  remoteStatus(remote: RemotePlayback, status: RemoteStatus): void {
    if (this.remote !== remote || !this.onRemote()) return;
    if (status.ended) {
      if (!this.state.finished) void this.finishBook();
      return;
    }
    const wasPlaying = this.state.isPlaying;
    if (status.error) {
      this.wantPlaying = false;
      this.patch({
        isPlaying: false,
        isBuffering: false,
        errorInfo: playerError('playbackFailed', status.error, { fatal: false }),
      });
    } else if (status.playing) {
      this.wantPlaying = true;
      this.pausedAt = 0;
      this.patch({ isPlaying: true, isBuffering: false, status: 'ready', error: null });
    } else if (status.buffering) {
      this.patch({ isBuffering: true });
    } else {
      this.wantPlaying = false;
      this.patch({ isPlaying: false, isBuffering: false });
    }
    if (wasPlaying && !status.playing && !status.buffering) {
      this.pausedAt = Date.now();
      void this.sync({ force: true });
    }
    this.remoteSample = null;
    this.patch({ currentTime: remote.position() });
    this.updatePositionState();
  }

  /** Loads the book on the receiver (unless it already plays it, as after a resumed session). */
  private async sendToRemote(autoplay: boolean, startAt = this.currentTime()) {
    const r = this.remote;
    const s = this.state;
    if (!r || !s.itemId || !s.loaded) return;
    if (r.itemId === s.itemId && r.hasMedia()) {
      r.adopt({ itemId: s.itemId, title: s.title, author: s.author, duration: s.duration, startAt: r.position(), rate: s.rate, autoplay: r.isPlaying() });
      this.wantPlaying = r.isPlaying();
      this.patch({ currentTime: r.position(), isPlaying: r.isPlaying(), isBuffering: false });
      return;
    }
    const itemId = s.itemId;
    this.wantPlaying = autoplay;
    this.patch({ isBuffering: autoplay, error: null });
    try {
      await r.load({ itemId, title: s.title, author: s.author, duration: s.duration, startAt, rate: s.rate, autoplay });
    } catch (err) {
      if (this.remote !== r || this.state.itemId !== itemId) return;
      this.wantPlaying = false;
      const message = err instanceof Error && err.message ? err.message : `Couldn't play this book on ${r.deviceName}.`;
      this.patch({ isPlaying: false, isBuffering: false, errorInfo: playerError('playbackFailed', message, { fatal: false }) });
    }
  }

  private playRemote(): Promise<void> {
    const r = this.remote!;
    const s = this.state;
    if (s.sleep.mode === 'timer' && s.sleep.endsAt != null && s.sleep.endsAt <= Date.now()) this.cancelSleepTimer('expired');
    if (r.itemId !== s.itemId || !r.hasMedia() || s.finished) {
      // Nothing of this book on the receiver yet (a failed load, Stop, the end of the book): send it.
      const at = s.finished ? 0 : this.onRemote() ? r.position() : this.state.currentTime;
      this.pausedAt = 0;
      if (s.finished) this.patch({ finished: false, currentTime: 0 });
      return this.sendToRemote(true, at);
    }
    if (settingsStore.get().smartRewind && this.pausedAt) {
      const back = smartRewindSeconds(Date.now() - this.pausedAt);
      if (back > 0) {
        this.nextSeek = { kind: 'smartRewind', chapterIndex: -1 };
        this.seek(Math.max(0, this.currentTime() - back));
      }
    }
    this.pausedAt = 0;
    this.wantPlaying = true;
    this.patch({ isBuffering: !s.isPlaying, error: null });
    r.play();
    return Promise.resolve();
  }

  private pauseRemote() {
    this.wantPlaying = false;
    this.remote!.pause();
    const was = this.state.isPlaying || this.state.isBuffering;
    this.patch({ isPlaying: false, isBuffering: false });
    if (was) {
      this.pausedAt = Date.now();
      void this.sync({ force: true });
    }
  }

  /** Once a second while casting: the receiver's position, listening time, sleep timer and periodic syncs. */
  private remoteTick() {
    const r = this.remote;
    if (!r || !this.onRemote()) return;
    const wall = Date.now();
    const t = r.position();
    const playing = r.isPlaying();
    const last = this.remoteSample;
    if (playing && last && last.seeks === this.seekCount) {
      // Listening time is what the playhead moved at the book's speed, never more than the
      // wall time: a phone asleep in a pocket wakes to a long gap, a paused TV adds nothing.
      const elapsed = (wall - last.wall) / 1000;
      const moved = (t - last.at) / Math.max(0.5, r.playbackRate());
      if (elapsed > 0 && moved > 0) this.listenedSinceSync += Math.min(elapsed, moved + 1);
    }
    this.remoteSample = { at: t, wall, seeks: this.seekCount };
    if (Math.abs(t - this.state.currentTime) > 0.2) this.patch({ currentTime: t });
    if (playing) this.checkSleep(t);
    const now = performance.now();
    if (this.currentChapterIndex(t) !== this.lastChapterForMetadata || now - this.lastPositionState > 10000) {
      this.lastPositionState = now;
      this.updatePositionState();
    }
    if (wall - this.lastLocalSave > LOCAL_SAVE_INTERVAL_MS) {
      this.lastLocalSave = wall;
      this.rememberLastPlayed();
    }
    if (playing && wall - this.lastServerSync > SERVER_SYNC_INTERVAL_MS) void this.sync();
    if (this.state.source === 'stream' && wall - this.lastTokenCheck > 60000) {
      this.lastTokenCheck = wall;
      void ensureFreshToken(5 * 60_000).catch(() => undefined);
    }
  }

  /** Moves the idle element to `target` without a seek event (playback coming back from a receiver). */
  private placeElement(target: number) {
    const { trackIndex, offset } = locateTrack(this.tracks, target);
    const track = this.tracks[trackIndex];
    if (!track) return;
    const a = this.el();
    if (trackIndex !== this.trackIndex && !track.isHls) {
      this.switchingTrack = true;
      void this.attachTrack(trackIndex, offset).catch(() => undefined);
      return;
    }
    const at = track.isHls ? target : offset;
    if (a.readyState >= 1 && !a.error) {
      try {
        a.currentTime = at;
        // A seek still waiting for 'canplay' would put the element back where casting began.
        if (this.pendingSeek != null) this.pendingSeek = at;
        return;
      } catch {
        /* fall through: applied once the element can play */
      }
    }
    this.pendingSeek = at;
  }

  // ------------------------------------------------------------ media session

  /**
   * iOS suspends a home-screen app soon after it goes to the background with
   * nothing playing, and its lock-screen controls go with it, so a book paused
   * for a moment could only be resumed from the app. Like a native player's
   * audio session, a short silent loop keeps the app (and the controls) alive
   * for `pausedHoldMs` after a pause. Started inside the pause tap or lock-screen
   * action, which is what lets iOS play it. Released by play, Stop, another
   * book, the sleep timer, the window running out, or another app taking audio.
   */
  private holdLockScreen() {
    if (this.pausedHoldMs <= 0 || this.stopping || typeof document === 'undefined') return;
    const h = this.holdElement();
    this.holding = true;
    if (this.holdTimer) clearTimeout(this.holdTimer);
    this.holdTimer = setTimeout(() => this.releaseLockScreen(), this.pausedHoldMs);
    h.play().catch(() => this.releaseLockScreen());
  }

  private releaseLockScreen() {
    if (this.holdTimer) clearTimeout(this.holdTimer);
    this.holdTimer = null;
    if (!this.holding) return;
    this.holding = false;
    this.holdAudio?.pause();
  }

  /** One element for every hold: once iOS let it play from a tap, it can play again from a lock-screen action. */
  private holdElement(): HTMLAudioElement {
    if (this.holdAudio) return this.holdAudio;
    const h = document.createElement('audio');
    h.setAttribute('playsinline', '');
    h.setAttribute('webkit-playsinline', '');
    h.loop = true;
    h.preload = 'auto';
    h.style.display = 'none';
    h.src = silentWavUrl(10);
    // Another app took the audio (music, a call): it owns the lock screen now.
    h.addEventListener('pause', () => {
      if (this.holding) this.releaseLockScreen();
    });
    // Keep the lock screen on the book's paused position, not the silence's.
    h.addEventListener('timeupdate', () => {
      if (this.holding) this.updatePositionState();
    });
    h.addEventListener('playing', () => {
      if (this.holding && 'mediaSession' in navigator) navigator.mediaSession.playbackState = 'paused';
    });
    document.body.appendChild(h);
    this.holdAudio = h;
    return h;
  }

  private setupMediaSession() {
    if (!('mediaSession' in navigator)) return;
    const ms = navigator.mediaSession;
    const set = (action: MediaSessionAction, handler: MediaSessionActionHandler | null) => {
      try {
        ms.setActionHandler(action, handler);
      } catch {
        /* action unsupported on this browser */
      }
    };
    // Like the in-app play button (ENG-009), play / pause do nothing while a book is loading:
    // the load already knows whether to start, and a pause would be undone by its autoplay.
    set('play', () => {
      if (this.state.status !== 'loading') void this.play();
    });
    set('pause', () => {
      if (this.state.status === 'loading') return;
      // While a pause holds the lock screen, iOS may show the silence as playing, with a pause button: it resumes the book.
      if (this.holding && !this.wantPlaying && this.audio?.paused) void this.play();
      else this.pause();
    });
    set('stop', () => void this.stop());
    set('seekbackward', (d) => this.skip(-(d.seekOffset ?? settingsStore.get().skipBackSeconds)));
    set('seekforward', (d) => this.skip(d.seekOffset ?? settingsStore.get().skipForwardSeconds));
    set('seekto', (d) => {
      if (d.seekTime != null) this.seek(d.seekTime);
    });
    // iOS (lock screen, Control Center, CarPlay) shows either track or skip
    // buttons, preferring track buttons. Audiobook listeners want skip there.
    if (platform.isIOS) {
      set('previoustrack', null);
      set('nexttrack', null);
    } else {
      set('previoustrack', () => this.previousChapter());
      set('nexttrack', () => this.nextChapter());
    }
  }

  private updateMetadata() {
    if (!('mediaSession' in navigator) || typeof MediaMetadata === 'undefined') return;
    const s = this.state;
    const art = s.coverUrl;
    const chapter = s.chapters[this.currentChapterIndex()]?.title;
    navigator.mediaSession.metadata = new MediaMetadata({
      title: s.title || 'Audiobook',
      artist: s.author,
      album: chapter ?? s.title,
      artwork: art
        ? [
            { src: art, sizes: '600x600', type: 'image/webp' },
            ...(s.itemId ? [{ src: coverUrl(s.itemId, 256), sizes: '256x256', type: 'image/webp' }] : []),
          ]
        : [{ src: '/icons/icon-512.png', sizes: '512x512', type: 'image/png' }],
    });
  }

  private lastChapterForMetadata = -1;
  private lastPositionState = 0;

  private updatePositionState() {
    if (!('mediaSession' in navigator)) return;
    const s = this.state;
    const ci = this.currentChapterIndex(this.currentTime());
    if (ci !== this.lastChapterForMetadata) {
      this.lastChapterForMetadata = ci;
      this.updateMetadata();
    }
    if (!navigator.mediaSession.setPositionState || !s.duration) return;
    try {
      navigator.mediaSession.setPositionState({
        duration: s.duration,
        position: Math.min(this.currentTime(), s.duration),
        playbackRate: this.audio?.playbackRate || 1,
      });
    } catch {
      /* invalid state during transitions */
    }
  }
}

/** True when the server answers a small range request for `url` successfully. */
async function serverServes(url: string): Promise<boolean> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 8000);
  try {
    const res = await fetch(url, { headers: { Range: 'bytes=0-1' }, credentials: 'omit', cache: 'no-store', signal: controller.signal });
    void res.body?.cancel().catch(() => undefined);
    return res.ok;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * A load failure as the error shown to the listener (code, message, action),
 * with Flutter's startup copy (ENG-007): a timeout → "Playback start timed
 * out…", 401 / 403 → "Playback authorization failed…", anything else →
 * "Unable to start playback. Please retry." The PWA keeps its own sentences
 * where Flutter has none: offline with the book not downloaded, a book gone
 * from the server (404, retrying cannot help) and a book without audio.
 */
export function classifyPlayerError(err: unknown): PlayerError {
  if (err instanceof PlayerFailure) return playerError(err.code, err.message);
  if (err instanceof AbsError) {
    if (err.isTimeout) return playerError('timeout', STARTUP_TIMED_OUT);
    if (err.isNetworkError) return playerError('network', STARTUP_FAILED);
    if (err.status === 404) return playerError('notFound', 'This book is no longer on the server, or it has no playable audio.', { status: 404 });
    if (err.status === 403) return playerError('forbidden', STARTUP_UNAUTHORIZED, { status: 403 });
    if (err.status === 401) return playerError('unauthorized', STARTUP_UNAUTHORIZED, { status: 401 });
    return playerError('unknown', STARTUP_FAILED, err.status ? { status: err.status } : {});
  }
  const name = (err as Error)?.name;
  const text = String((err as Error)?.message ?? '');
  if (name === 'TimeoutError' || /timed out/i.test(text)) return playerError('timeout', STARTUP_TIMED_OUT);
  if (/\b40[13]\b/.test(text)) return playerError('unauthorized', STARTUP_UNAUTHORIZED);
  return playerError('unknown', STARTUP_FAILED);
}

/** A session failure the direct files may get around: not auth, not a missing book, not the format, and online. */
function canFallBackToDirect(err: unknown): boolean {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return false;
  if (err instanceof PlayerFailure) return false;
  if ((err as Error)?.name === 'AbortError') return false;
  if (err instanceof AbsError) return err.status !== 401 && err.status !== 403 && err.status !== 404;
  return false;
}

/** The audio element gave up on the current source. */
export function mediaFailure(source: PlayerState['source'], code: number | undefined, online: boolean): PlayerError {
  const unsupported = () => playerError('unsupportedFormat', "This browser can't play this book's audio format.");
  if (source === 'local') {
    if (code === MEDIA_ERR_SRC_NOT_SUPPORTED) return unsupported();
    // A downloaded book needs no connection: the stored copy itself is the problem.
    return playerError('downloadUnplayable', "This download couldn't be played. It may be incomplete or corrupt; download it again to listen.");
  }
  // Browsers report failed requests as "not supported" too, so being offline explains it first.
  if (!online) return playerError('offline', "You're offline. Download books to listen without a connection.");
  if (code === MEDIA_ERR_SRC_NOT_SUPPORTED) return unsupported();
  return playerError('playbackFailed', 'Playback failed. Check your connection and try again.');
}

/** `MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED` (a constant, so this module loads where MediaError is missing). */
const MEDIA_ERR_DECODE = 3;
const MEDIA_ERR_SRC_NOT_SUPPORTED = 4;

export const player = new Player();
